import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { browser, isExtension } from '@web/constants/browserapi'
import { storage } from '@web/extension-services/background/webapi/storage'
import eventBus from '@web/extension-services/event/eventBus'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import useAccountsControllerState from '@web/hooks/useAccountsControllerState'
import useKeystoreControllerState from '@web/hooks/useKeystoreControllerState'
import useAppMode from '@web/hooks/useAppMode'
import useControllerState from '@web/hooks/useControllerState'
import {
  type RailgunTokenBalanceMap,
  combineRailgunBucketBalances,
  fetchTokenPricesForBalanceMap,
  getTotalBalanceUSD,
  isWrappedBaseToken,
  mergeRailgunBalanceMaps,
  priceKey,
  ZERO_ADDRESS
} from '@web/utils/railgunTokens'
import { ExtendedPOIProofEventStatus, getNetworksForBalance } from '@web/utils/railgunCore'
import {
  shieldBaseTokenWithSDK,
  estimateShieldGasBaseToken,
  shieldERC20WithSDK,
  estimateShieldGasERC20
} from '@web/utils/railgunShield'
import { appendRailgunShieldTxRecord } from '@web/utils/railgunShieldTxStorage'
import {
  estimateUnshieldGasERC20,
  estimateUnshieldGasBaseToken,
  type PreparedUnshieldGasDetails,
  type UnshieldGasEstimation,
  generateUnshieldProofERC20WithSDK,
  executePreparedUnshieldERC20WithSDK,
  generateUnshieldProofBaseTokenWithSDK,
  executePreparedUnshieldBaseTokenWithSDK
} from '@web/utils/railgunUnshield'
import {
  getUnshieldToOriginRecipientsWithSDK,
  estimateUnshieldToOriginGas as estimateUnshieldToOriginGasWithSDK,
  generateUnshieldToOriginProofWithSDK,
  executePreparedUnshieldToOriginWithSDK,
  type UnshieldToOriginRecipients,
  type PreparedUnshieldToOriginGasDetails
} from '@web/utils/railgunUnshieldToOrigin'
import {
  estimatePrivateTransferGas as estimatePrivateTransferGasUtil,
  generatePrivateTransferProofWithSDK,
  executePreparedPrivateTransferWithSDK,
  type PreparedPrivateTransferGasDetails,
  type PrivateTransferGasEstimation
} from '@web/utils/railgunTransfer'
import {
  PRIVATE_BALANCE_BACKGROUND_SNAPSHOT_STORAGE_KEY,
  PrivateTransferPopupPreflightCode,
  getPrivateBalanceSnapshotEntryKey,
  type PrivateBalanceSnapshot
} from '@web/utils/railgunPrivateTransferPopupPreflight'
import type {
  TransactionHistoryItem,
  POIProofProgressEvent
} from '@railgun-community/shared-models'
import {
  RailgunWallet,
  createRailgunWallet,
  loadRailgunWalletById,
  persistedRailgunWalletsEqual,
  RAILGUN_DEFAULT_ACCOUNT_INDEX,
  type RailgunBalanceResult
} from '@web/utils/railgunWallet'
import { ModeEnum } from '@web/contexts/appModeContext'
import {
  NetworkName,
  NETWORK_CONFIG,
  MerkletreeScanStatus,
  type MerkletreeScanUpdateEvent
} from '@railgun-community/shared-models'
import {
  getPOIRequiredForNetworkForChain,
  refreshReceivePOIsForWalletForChain,
  isNoteAlreadySpentError
} from '@web/utils/railgunShared'
import { FEATURES } from '@web/constants/features'
import { isMobileBrowser } from '@common/config/env'
import {
  DEFAULT_MOBILE_PRIVATE_CHAIN_ID,
  EXTENSION_PRIVATE_CHAIN_IDS
} from '@web/constants/extensionChains'
import {
  createWalletFromSeedInOffscreen,
  isWalletPrimaryRecordMissingError,
  loadWalletByIdInOffscreen,
  OffscreenRailgunWalletProxy,
  refreshReceivePOIsInOffscreen
} from '@web/extension-services/background/offscreen/railgunOffscreenClient'
import {
  awaitRailgunWalletResult,
  nextRailgunWalletRequestId,
  type RailgunWalletPublicInfo
} from '../../../mobile-web/services/railgunWorker/railgunWorkerBridge'
import { RailgunMerkletreeScanProvider } from '@web/contexts/railgunContext/railgunMerkletreeScanContext'
import {
  isWithinWalletInstanceBackoff,
  registerWalletInstanceFailure,
  scheduleWalletInstanceRetryRerun,
  type WalletInstanceRetryState
} from '@web/contexts/railgunContext/walletInstanceRetry'

// A1 keystore-isolation: ceiling for a page → worker RPC. Deliberately long
// for the same reason the offscreen `SDK_INVOKE` timeout is long — balance/proof
// work can take minutes. Fast calls (createWallet) resolve early; this is only a
// safety ceiling.
// MUST stay strictly above `getBalance`'s own inner fallback
// (`BALANCE_WAIT_TIMEOUT_MS` = 20 min in railgunWallet.ts) and the nested
// `RAILGUN_RPC_TIMEOUT_MS` (22 min). Previously 16 min — BELOW the 20-min inner
// fallback — so any 16-20 min first scan (e.g. a fresh Ethereum chain) rejected
// here with "railgun worker request timed out" and surfaced a false
// "Couldn't load your private balance" while the scan was still completing fine.
const RAILGUN_WORKER_WALLET_TIMEOUT_MS = 23 * 60 * 1000

// A1 keystore-isolation (§13 of docs/phase4-railgun-isolation-vi.md): page-side
// proxy for a railgun wallet whose engine + secret (encryptionKey) live in the
// nested railgun worker. Holds only PUBLIC identity. `getEncryptionKey` returns
// '' by design — the key never reaches the page. Balance / history forward to the
// worker over the injected `transport` (page → keystore worker → railgun worker),
// keyed only by walletId; no secret crosses back.
type RailgunWorkerTransport = (method: string, params: any) => Promise<any>

class WorkerRailgunWalletProxy {
  #info: RailgunWalletPublicInfo

  #transport: RailgunWorkerTransport

  constructor(info: RailgunWalletPublicInfo, transport: RailgunWorkerTransport) {
    this.#info = info
    this.#transport = transport
  }

  getWalletId(): string {
    return this.#info.walletId
  }

  // Intentionally empty: under A1 the encryptionKey is never on the page.
  getEncryptionKey(): string {
    return ''
  }

  async getRailgunAddress(): Promise<string> {
    return this.#info.railgunAddress
  }

  async getShareableViewingKey(): Promise<string> {
    return this.#info.viewingKey
  }

  async getBalance(networks: NetworkName[]): Promise<RailgunBalanceResult> {
    return this.#transport('getBalance', { walletId: this.#info.walletId, networks })
  }

  async getTransactionHistory(
    networkName: NetworkName,
    startingBlock?: number
  ): Promise<TransactionHistoryItem[]> {
    return this.#transport('getTransactionHistory', {
      walletId: this.#info.walletId,
      networkName,
      startingBlock
    })
  }
}

// Wallet-instance helpers — branch on `railgunEngineOffscreenEnabled`. The proxy mirrors
// the RailgunWallet surface used by callers below, so the rest of the file stays uniform.
type WalletInstance = RailgunWallet | OffscreenRailgunWalletProxy | WorkerRailgunWalletProxy

async function loadWalletInstanceById(
  walletId: string,
  encryptionKey: string
): Promise<WalletInstance> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    const info = await loadWalletByIdInOffscreen({ walletId, encryptionKey })
    return new OffscreenRailgunWalletProxy(info)
  }
  return loadRailgunWalletById(walletId, encryptionKey)
}

async function createWalletInstanceFromSeed(
  mnemonic: string,
  passphrase?: string
): Promise<{ wallet: WalletInstance; railgunAddress: string; viewingKey: string }> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    const info = await createWalletFromSeedInOffscreen({
      mnemonic,
      accountIndex: RAILGUN_DEFAULT_ACCOUNT_INDEX,
      passphrase
    })
    return {
      wallet: new OffscreenRailgunWalletProxy(info),
      railgunAddress: info.railgunAddress,
      viewingKey: info.viewingKey
    }
  }
  const wallet = await createRailgunWallet(mnemonic, RAILGUN_DEFAULT_ACCOUNT_INDEX, passphrase)
  const railgunAddress = await wallet.getRailgunAddress()
  const viewingKeyPair = await wallet.getViewingKeyPair()
  const viewingKey = `0x${Buffer.from(viewingKeyPair.privateKey).toString('hex')}`
  return { wallet, railgunAddress, viewingKey }
}

async function loadOrCreateWalletInstanceForAccount(
  account: { addr: string },
  storedWallets: Array<{ accountAddr: string; walletId?: string; encryptionKey?: string }>,
  retrieveSeedPhrase: (accountAddr: string) => Promise<{ seed: string; seedPassphrase?: string }>
): Promise<WalletInstance> {
  const storedWallet = storedWallets.find(
    (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
  )
  if (storedWallet?.walletId && storedWallet?.encryptionKey) {
    return loadWalletInstanceById(storedWallet.walletId, storedWallet.encryptionKey)
  }
  const seedPhrase = await retrieveSeedPhrase(account.addr)
  const created = await createWalletInstanceFromSeed(seedPhrase.seed, seedPhrase.seedPassphrase)
  return created.wallet
}

async function loadWalletInstanceFromStorageOnly(
  account: { addr: string },
  storedWallets: Array<{ accountAddr: string; walletId?: string; encryptionKey?: string }>
): Promise<WalletInstance | null> {
  const storedWallet = storedWallets.find(
    (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
  )
  if (!storedWallet?.walletId || !storedWallet?.encryptionKey) return null
  return loadWalletInstanceById(storedWallet.walletId, storedWallet.encryptionKey)
}

export interface RailgunWalletData {
  accountAddr: string
  railgunAddress: string
  viewingKey: string
  createdAt: number
  walletId?: string
  encryptionKey?: string
  balance?: bigint
  balanceTokenMap?: RailgunTokenBalanceMap
  pendingBalanceTokenMap?: RailgunTokenBalanceMap
  missingInternalPOIBalanceTokenMap?: RailgunTokenBalanceMap
  missingExternalPOIBalanceTokenMap?: RailgunTokenBalanceMap
  balanceTokenPriceMap?: Map<string, number>
  totalBalanceUSD?: number
  balanceLastUpdatedAt?: number
  /**
   * Set when the local UTXO merkle root for any synced chain disagrees with the on-chain
   * RailgunSmartWallet `rootHistory`. Indicates stale/poisoned local cache — spends must
   * be blocked until a fresh sync produces a matching root. Phase 0.2 of
   * RAILGUN_HYBRID_REWRITE_PLAN.md.
   */
  cachePoisoned?: boolean
  cachePoisonedNetworks?: string[]
}

import type { RailgunGasSpeedName } from '@web/utils/railgunShared'

export interface ShieldParams {
  amount: bigint
  recipientAddress: string
  chainId: number
  /** When provided and not the wrapped base token, ERC20 shield is used; otherwise base-token shield. */
  tokenAddress?: string
  /** Optional gas speed (slow/medium/fast/ape) for the public shield tx. */
  feeSpeed?: RailgunGasSpeedName
}

export interface UnshieldParams {
  amount: bigint
  recipientAddress: string
  chainId: number
  tokenAddress: string
  /** Optional gas speed (slow/medium/fast/ape) for the unshield tx. */
  feeSpeed?: RailgunGasSpeedName
}

/** Recover a POI-blocked shield back to its origin public address. Recipient is
 * SDK-derived (not user-chosen); self-signed + self-paid gas (no broadcaster). */
export interface UnshieldToOriginParams {
  chainId: number
  originalShieldTxid: string
  /** SDK-derived origin recipients (token + amount + origin address). Fetched via
   * `getUnshieldToOriginRecipients`, threaded through estimate/proof unchanged. */
  recipients: UnshieldToOriginRecipients
  /** Token address being recovered — used only for the in-flight guard. */
  tokenAddress?: string
  feeSpeed?: RailgunGasSpeedName
}

/** ERC20-only. recipientRailgunAddress is the 0zk... railgun address. */
export interface PrivateTransferParams {
  amount: bigint
  recipientRailgunAddress: string
  chainId: number
  tokenAddress: string
  memoText?: string
  /** Optional gas speed (slow/medium/fast/ape) for the private transfer tx. */
  feeSpeed?: RailgunGasSpeedName
}

import type { ShieldGasEstimation } from '@web/utils/railgunShield'
import { emitRailgunEvent, railgunEmitter } from '@web/utils/railgunListener'
import type { RailgunBalancesEvent } from '@railgun-community/shared-models'

export interface RailgunContextValue {
  currentRailgunWallet: RailgunWalletData | null
  allRailgunWallets: RailgunWalletData[]
  isLoading: boolean
  isBalanceFetching: boolean
  isPriceFetching: boolean
  isBalanceFetchingError: boolean
  privateHistory: Array<TransactionHistoryItem & { networkName: NetworkName }>
  isPrivateHistoryLoading: boolean
  privateHistoryError: string | null
  refreshPrivateHistory: (opts?: { force?: boolean }) => Promise<void>
  fetchBalance: () => Promise<void>
  triggerPOISync: () => void
  shieldTo: (params: ShieldParams) => Promise<void>
  estimateShieldGas: (params: ShieldParams) => Promise<ShieldGasEstimation | null>
  unshieldFrom: (params: UnshieldParams) => Promise<void>
  generateUnshieldProof: (params: UnshieldParams) => Promise<PreparedUnshieldGasDetails | null>
  executeUnshieldFromProof: (
    params: UnshieldParams,
    gasDetails: PreparedUnshieldGasDetails
  ) => Promise<void>
  estimateUnshieldGas: (params: UnshieldParams) => Promise<UnshieldGasEstimation | null>
  // --- unshield to origin (POI-blocked shield recovery) ---
  getUnshieldToOriginRecipients: (params: {
    chainId: number
    originalShieldTxid: string
  }) => Promise<UnshieldToOriginRecipients | null>
  estimateUnshieldToOriginGas: (
    params: UnshieldToOriginParams
  ) => Promise<UnshieldGasEstimation | null>
  generateUnshieldToOriginProof: (
    params: UnshieldToOriginParams
  ) => Promise<PreparedUnshieldToOriginGasDetails | null>
  executeUnshieldToOriginFromProof: (
    params: UnshieldToOriginParams,
    gasDetails: PreparedUnshieldToOriginGasDetails
  ) => Promise<void>
  estimatePrivateTransferGas: (
    params: PrivateTransferParams
  ) => Promise<PrivateTransferGasEstimation | null>
  generatePrivateTransferProof: (
    params: PrivateTransferParams
  ) => Promise<PreparedPrivateTransferGasDetails | null>
  executePrivateTransferFromProof: (
    params: PrivateTransferParams,
    gasDetails: PreparedPrivateTransferGasDetails
  ) => Promise<string>
  checkPrivateTransferPopupPreflight: (params: {
    chainId: number
    tokenAddress: string
  }) => Promise<PrivateTransferPopupPreflightCode | null>
  getPrivateTransactionHistory: () => Promise<
    Array<TransactionHistoryItem & { networkName: NetworkName }>
  >
  /**
   * True while an unshield on `chainId` is broadcast-but-unsettled. Consumers must
   * block generating a new unshield proof on that chain to avoid the SDK reusing
   * not-yet-spent notes ("SnarkJS failed to fullProveRailgun").
   */
  isUnshieldInFlight: (chainId?: number) => boolean
}

const RailgunContext = createContext<RailgunContextValue>({
  currentRailgunWallet: null,
  allRailgunWallets: [],
  isLoading: true,
  isBalanceFetching: false,
  isPriceFetching: false,
  isBalanceFetchingError: false,
  privateHistory: [],
  isPrivateHistoryLoading: false,
  privateHistoryError: null,
  refreshPrivateHistory: async () => {
    throw new Error('refreshPrivateHistory not implemented')
  },
  fetchBalance: async () => {
    throw new Error('fetchBalance not implemented')
  },
  triggerPOISync: () => {
    throw new Error('triggerPOIToast not implemented')
  },
  shieldTo: async () => {
    throw new Error('shieldTo not implemented')
  },
  estimateShieldGas: async () => {
    throw new Error('estimateShieldGas not implemented')
  },
  unshieldFrom: async () => {
    throw new Error('unshieldFrom not implemented')
  },
  generateUnshieldProof: async () => {
    throw new Error('generateUnshieldProof not implemented')
  },
  executeUnshieldFromProof: async () => {
    throw new Error('executeUnshieldFromProof not implemented')
  },
  estimateUnshieldGas: async () => {
    throw new Error('estimateUnshieldGas not implemented')
  },
  getUnshieldToOriginRecipients: async () => {
    throw new Error('getUnshieldToOriginRecipients not implemented')
  },
  estimateUnshieldToOriginGas: async () => {
    throw new Error('estimateUnshieldToOriginGas not implemented')
  },
  generateUnshieldToOriginProof: async () => {
    throw new Error('generateUnshieldToOriginProof not implemented')
  },
  executeUnshieldToOriginFromProof: async () => {
    throw new Error('executeUnshieldToOriginFromProof not implemented')
  },
  estimatePrivateTransferGas: async () => {
    throw new Error('estimatePrivateTransferGas not implemented')
  },
  generatePrivateTransferProof: async () => {
    throw new Error('generatePrivateTransferProof not implemented')
  },
  executePrivateTransferFromProof: async () => {
    throw new Error('executePrivateTransferFromProof not implemented')
  },
  checkPrivateTransferPopupPreflight: async () => {
    throw new Error('checkPrivateTransferPopupPreflight not implemented')
  },
  getPrivateTransactionHistory: async () => [],
  isUnshieldInFlight: () => false
})

/**
 * Reconcile the persisted identity rows loaded from storage with the live in-memory rows.
 *
 * Token balance maps are in-memory only — they are NEVER persisted (see `loadRailgunWallets`).
 * A naive `setRailgunWallets(stored)` therefore wipes the per-token maps + the freshly fetched
 * `totalBalanceUSD` that `fetchBalance` merged in, dropping the dashboard back to "$0, no tokens".
 *
 * On the mobile-as-webapp target the storage backing is the in-process ControllerStore, which can
 * transiently report `[]` for `railgunBackgroundWallets` during a worker re-init / re-emit window
 * (see the engine-ready churn). Committing that empty snapshot zeroes the wallet list (`found:
 * false`) until the next emit re-populates it — the observed 16$→0→16$→0 flicker.
 *
 * Rules:
 *  - A transient empty `stored` never wipes live rows.
 *  - For each persisted row, carry over the live row's in-memory balance fields (token maps +
 *    price map), and keep the fresher `totalBalanceUSD` / `balanceLastUpdatedAt` so an in-flight
 *    in-page fetch isn't clobbered by a stale persisted headline.
 */
function reconcilePersistedRailgunWallets(
  prev: RailgunWalletData[],
  stored: RailgunWalletData[]
): RailgunWalletData[] {
  if (stored.length === 0) return prev
  const prevByAddr = new Map(prev.map((w) => [w.accountAddr.toLowerCase(), w]))
  let changed = prev.length !== stored.length
  const next = stored.map((s) => {
    const p = prevByAddr.get(s.accountAddr.toLowerCase())
    if (!p) {
      changed = true
      return s
    }
    // Prefer the live balance fields whenever the in-memory row already carries a fetched
    // token map or a newer headline timestamp than what's persisted on disk.
    const liveIsFresher =
      (p.balanceTokenMap?.size ?? 0) > 0 ||
      (p.balanceLastUpdatedAt ?? 0) >= (s.balanceLastUpdatedAt ?? 0)
    const merged: RailgunWalletData = {
      ...s,
      balance: p.balance ?? s.balance,
      balanceTokenMap: p.balanceTokenMap ?? s.balanceTokenMap,
      pendingBalanceTokenMap: p.pendingBalanceTokenMap ?? s.pendingBalanceTokenMap,
      missingInternalPOIBalanceTokenMap:
        p.missingInternalPOIBalanceTokenMap ?? s.missingInternalPOIBalanceTokenMap,
      missingExternalPOIBalanceTokenMap:
        p.missingExternalPOIBalanceTokenMap ?? s.missingExternalPOIBalanceTokenMap,
      balanceTokenPriceMap: p.balanceTokenPriceMap ?? s.balanceTokenPriceMap,
      totalBalanceUSD: liveIsFresher ? p.totalBalanceUSD : s.totalBalanceUSD,
      balanceLastUpdatedAt: liveIsFresher ? p.balanceLastUpdatedAt : s.balanceLastUpdatedAt,
      cachePoisoned: p.cachePoisoned ?? s.cachePoisoned,
      cachePoisonedNetworks: p.cachePoisonedNetworks ?? s.cachePoisonedNetworks
    }
    if (merged.totalBalanceUSD !== s.totalBalanceUSD || (p.balanceTokenMap?.size ?? 0) > 0) {
      changed = true
    }
    return merged
  })
  return changed ? next : prev
}

/**
 * Worker-build identity cache (mobile-as-webapp).
 *
 * On the extension the SW-side RailgunBackgroundController is the sole writer of the
 * `railgunBackgroundWallets` store. On mobile-web that controller is a stub and the page
 * never holds an `encryptionKey`, so the normal `RAILGUN_BACKGROUND_CONTROLLER_PERSIST_WALLET`
 * dispatch is skipped (see the persist loop in `handleLoadRailgunWallets`). Result: a cold
 * session that hasn't entered private mode has no accountAddr→railgunAddress mapping, so the
 * Shield/Unshield recipient picker renders "No contacts found" until the user switches into
 * the private wallet (which lazily boots the engine).
 *
 * Persist only the *public* identity (railgunAddress/viewingKey/walletId — never the
 * encryptionKey, which the worker re-derives from the seed, and never the in-memory-only
 * balance maps) directly to the same key `loadRailgunWallets` reads, so the mapping survives
 * across sessions. Upsert by accountAddr; preserve any balance headline already persisted.
 */
async function persistRailgunIdentityRowsLocally(rows: RailgunWalletData[]): Promise<void> {
  if (!rows.length) return
  try {
    const stored = (await storage.get('railgunBackgroundWallets', [])) as RailgunWalletData[]
    const byAddr = new Map(stored.map((w) => [w.accountAddr.toLowerCase(), w]))
    let changed = false
    for (const row of rows) {
      if (!row.railgunAddress) continue
      const key = row.accountAddr.toLowerCase()
      const existing = byAddr.get(key)
      const identity: RailgunWalletData = {
        accountAddr: row.accountAddr,
        railgunAddress: row.railgunAddress,
        viewingKey: row.viewingKey,
        createdAt: row.createdAt,
        walletId: row.walletId,
        // Carry over a previously persisted balance headline (in-memory maps stay unpersisted).
        ...(existing?.totalBalanceUSD != null ? { totalBalanceUSD: existing.totalBalanceUSD } : {}),
        ...(existing?.balanceLastUpdatedAt != null
          ? { balanceLastUpdatedAt: existing.balanceLastUpdatedAt }
          : {})
      }
      if (
        !existing ||
        existing.railgunAddress !== identity.railgunAddress ||
        existing.walletId !== identity.walletId
      ) {
        changed = true
      }
      byAddr.set(key, identity)
    }
    if (!changed) return
    await storage.set('railgunBackgroundWallets', Array.from(byAddr.values()))
  } catch (error) {
    console.error('[Railgun] failed to persist identity cache locally:', error)
  }
}

/**
 * On mobile-device browsers, private-mode balance scanning is clamped to a single
 * chain — scanning all 3 RAILGUN chains at once OOM-crashes resource-constrained
 * mobile browsers. "All Networks" (null) or an unsupported chain falls back to
 * BNB. Desktop browsers and the extension are unaffected (returns filter as-is).
 */
// Mobile-web has no SW summary mirror, so per-chain "first sync done" is tracked
// in-page keyed by `${accountAddr}:${chainId}`. Keying per chain (not per account)
// is what lets a freshly-selected, never-scanned chain still report
// `isInitialPrivateSync = true` even after another chain on the same account has
// already completed — without it, switching BNB→Ethereum shows an empty token
// list as if done while Ethereum is still scanning.
const inPageChainKey = (accountAddr: string, chainId: number): string =>
  `${accountAddr.toLowerCase()}:${chainId}`

const chainIdsForNetworks = (networks: NetworkName[]): number[] =>
  networks.map((name) => NETWORK_CONFIG[name].chain.id)

function resolvePrivateBalanceFilter(
  filter: number | bigint | string | null
): number | bigint | string | null {
  if (!isMobileBrowser) return filter
  const id = filter == null ? null : Number(filter)
  if (id == null || Number.isNaN(id) || !EXTENSION_PRIVATE_CHAIN_IDS.includes(id)) {
    return DEFAULT_MOBILE_PRIVATE_CHAIN_ID
  }
  return filter
}

// Max time an unshield stays locked per-chain after broadcast, in case the tx
// stalls and no balance-drop scan ever lands. Covers slow-chain confirmation.
const UNSHIELD_INFLIGHT_TIMEOUT_MS = 5 * 60 * 1000

const RailgunContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { railgunEngineInitialized, mode, ensureRailgunEngine } = useAppMode()
  const isPrivateMode = mode === ModeEnum.PRIVATE

  const [railgunWallets, _setRailgunWallets] = useState<RailgunWalletData[]>([])
  // TEMP ZERODIAG: wrap the setter to capture the CALLER stack whenever a write
  // zeros a row that previously had a positive total or non-empty token map.
  // Stack is captured synchronously at call time (the deferred updater's own stack
  // would be React internals). Names the exact setRailgunWallets site that zeros.
  const setRailgunWallets = (
    arg: RailgunWalletData[] | ((prev: RailgunWalletData[]) => RailgunWalletData[])
  ) => {
    const stack = new Error().stack?.split('\n').slice(2, 6).join(' || ')
    _setRailgunWallets((prev) => {
      const next = typeof arg === 'function' ? (arg as any)(prev) : arg
      try {
        const zeroed = (next as RailgunWalletData[]).some((w) => {
          const pmatch = prev.find(
            (x) => x.accountAddr.toLowerCase() === w.accountAddr.toLowerCase()
          )
          const wasPos =
            (pmatch?.totalBalanceUSD ?? 0) > 0 || (pmatch?.balanceTokenMap?.size ?? 0) > 0
          const nowZero = (w.totalBalanceUSD ?? 0) === 0 && !((w.balanceTokenMap?.size ?? 0) > 0)
          return wasPos && nowZero
        })
        if (zeroed) {
          // eslint-disable-next-line no-console
          console.log('[ZERODIAG SETZERO] a row went positive→zero; caller=', stack)
        }
      } catch {
        /* ignore */
      }
      return next
    })
  }

  const [isLoading, setIsLoading] = useState(false)

  const [isBalanceFetching, setIsBalanceFetching] = useState(false)
  const [isPriceFetching, setIsPriceFetching] = useState(false)
  const [isBalanceFetchingError, setIsBalanceFetchingError] = useState(false)

  const [privateHistory, setPrivateHistory] = useState<
    Array<TransactionHistoryItem & { networkName: NetworkName }>
  >([])
  const [isPrivateHistoryLoading, setIsPrivateHistoryLoading] = useState(false)
  const [privateHistoryError, setPrivateHistoryError] = useState<string | null>(null)

  // Default-unwrap because on the mobile-as-webapp target these hooks read from
  // the in-process ControllerStore via the `.native` shims, and that store may
  // expose an empty `{}` state for a few ticks before INIT_ALL_CONTROLLERS lands.
  // Without the defaults, `accounts.length` below throws during initial render.
  const { account, dashboardNetworkFilter } = useSelectedAccountControllerState() || {}
  const accountsState = useAccountsControllerState() || {}
  const accounts = Array.isArray(accountsState.accounts) ? accountsState.accounts : []
  const keystoreState = useKeystoreControllerState() || {}
  const keys = Array.isArray(keystoreState.keys) ? keystoreState.keys : []
  const { isUnlocked } = keystoreState
  const { dispatch } = useBackgroundService()

  const [firstFetchDone, setFirstFetchDone] = useState(false)
  // When `railgunEngineOffscreenEnabled` is on the engine lives in the offscreen document and
  // the UI holds a thin proxy. Both branches expose the same surface used below
  // (getWalletId/getEncryptionKey/getRailgunAddress/getBalance/getTransactionHistory).
  const [walletInstances, setWalletInstances] = useState<Map<string, WalletInstance>>(new Map())

  // Refs to keep track of the current processing account, retrieve seed phrase, fetch balance, and railgun wallets
  const processingAccountRef = useRef<string | null>(null)
  // Backoff guard for railgun wallet-instance creation. The page-side
  // `railgunEngineInitialized` flag can race ahead of the worker SDK, so a freshly
  // added account hits a transient `RAILGUN Engine not yet initialized` throw. The
  // `catch` below clears `processingAccountRef`, and the balance-update storm churns
  // `walletInstances` identity ~7×/s, which re-fires the create effect every render.
  // Without a cooldown that retry loop melts the in-process worker and starves
  // unrelated flows (e.g. account-picker derive → infinite loading). Keyed per
  // account: skip while inside the window, exponential backoff on each failure.
  //
  // BUT the balance-update churn only exists once a wallet instance is created.
  // When the VERY FIRST create fails there is no instance → no getBalance → no
  // `balanceUpdate` → `walletInstances` never churns → the create effect never
  // re-fires and the backoff timestamp is never re-read → private balance stays
  // empty forever (observed cold on app.dev.zkxwallet.com). So on failure we also
  // arm a timer that bumps `walletInstanceRetryTick` (a dep of the effect) to
  // actively re-fire it, independent of any balance churn. See walletInstanceRetry.ts.
  const walletInstanceRetryRef = useRef<Map<string, WalletInstanceRetryState>>(new Map())
  const walletInstanceRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [walletInstanceRetryTick, setWalletInstanceRetryTick] = useState(0)
  const retrieveSeedPhraseRef = useRef<
    ((accountAddr: string) => Promise<{ seed: string; seedPassphrase?: string }>) | null
  >(null)
  const fetchBalanceRef = useRef<(() => Promise<void>) | null>(null)
  const railgunWalletsRef = useRef<RailgunWalletData[]>([])
  const isPrivateModeRef = useRef(isPrivateMode)
  const hasFetchedBalanceThisPrivateSessionRef = useRef(false)
  // Per-session set of (account, network-filter) scopes already fetched while in
  // private mode. Keeps the private wallet consistent across public↔private
  // toggling: a scope is scanned/fetched once per unlocked session, then served
  // from cached state (kept live by the `balanceUpdate` listener). Re-entering
  // private with the same scope no longer re-runs getBalance → merkletree scan.
  // Cleared on re-unlock and on account change (same lifecycle as the latch).
  const fetchedPrivateScopesRef = useRef<Set<string>>(new Set())

  // --- Unshield-in-flight guard ---------------------------------------------
  // After an unshield broadcasts it sits "Pending" on-chain; its source notes
  // stay locally spendable until the nullifier is scanned. Generating a SECOND
  // unshield proof in that window re-selects the same notes and builds an invalid
  // witness, so the SDK throws "SnarkJS failed to fullProveRailgun". We block new
  // unshield proofs per-chain from broadcast until a balance refresh shows the
  // token's spendable balance dropped (the spend landed during a scan), with a
  // timeout fallback so the UI never latches if the tx stalls.
  const [unshieldInFlightChains, setUnshieldInFlightChains] = useState<number[]>([])
  const unshieldInFlightRef = useRef<
    Map<
      number,
      {
        tokenAddress: string
        snapshotSpendable: bigint
        broadcastAt: number
        timer: ReturnType<typeof setTimeout>
      }
    >
  >(new Map())

  const getSpendableForToken = useCallback(
    (chainId: number, tokenAddress: string): bigint => {
      const addr = account?.addr
      if (!addr) return 0n
      const wallet = railgunWalletsRef.current.find(
        (w) => w.accountAddr.toLowerCase() === addr.toLowerCase()
      )
      const entries = wallet?.balanceTokenMap?.get(chainId)
      if (!entries) return 0n
      const lower = tokenAddress.toLowerCase()
      return entries
        .filter((e) => e.tokenAddress.toLowerCase() === lower)
        .reduce((sum, e) => sum + e.amount, 0n)
    },
    [account?.addr]
  )

  const clearUnshieldInFlight = useCallback((chainId: number) => {
    const entry = unshieldInFlightRef.current.get(chainId)
    if (!entry) return
    clearTimeout(entry.timer)
    unshieldInFlightRef.current.delete(chainId)
    setUnshieldInFlightChains(Array.from(unshieldInFlightRef.current.keys()))
  }, [])

  const markUnshieldInFlight = useCallback(
    (chainId: number, tokenAddress: string) => {
      const prev = unshieldInFlightRef.current.get(chainId)
      if (prev) clearTimeout(prev.timer)
      const timer = setTimeout(() => clearUnshieldInFlight(chainId), UNSHIELD_INFLIGHT_TIMEOUT_MS)
      unshieldInFlightRef.current.set(chainId, {
        tokenAddress,
        snapshotSpendable: getSpendableForToken(chainId, tokenAddress),
        broadcastAt: Date.now(),
        timer
      })
      setUnshieldInFlightChains(Array.from(unshieldInFlightRef.current.keys()))
    },
    [clearUnshieldInFlight, getSpendableForToken]
  )

  const isUnshieldInFlight = useCallback(
    (chainId?: number) => (chainId == null ? false : unshieldInFlightChains.includes(chainId)),
    [unshieldInFlightChains]
  )

  // Auto-clear a chain's lock once a balance refresh shows the spent notes are
  // gone (spendable dropped below the broadcast snapshot). `railgunWallets` is the
  // balance-state source, so this re-runs on every balance update / scan flush.
  useEffect(() => {
    if (unshieldInFlightRef.current.size === 0) return
    for (const [chainId, info] of Array.from(unshieldInFlightRef.current.entries())) {
      if (getSpendableForToken(chainId, info.tokenAddress) < info.snapshotSpendable) {
        clearUnshieldInFlight(chainId)
      }
    }
  }, [railgunWallets, getSpendableForToken, clearUnshieldInFlight])
  // Guard against overlapping in-page balance fetches on mobile-as-webapp. Each
  // `walletInstance.getBalance(networks)` call drives `refreshBalances(chain, ...)`
  // per chain inside the SDK and can take tens of seconds; without this lock,
  // the auto-refetch-on-scan-complete + dashboard-filter-change + account-change
  // effects can all race a second fetch on top of an in-flight one.
  const inPageFetchInFlightRef = useRef(false)
  // Same overlap problem as `inPageFetchInFlightRef`, but for the private
  // transaction-history sweep. `refreshPrivateHistory` runs a full
  // `getTransactionHistory` across every RAILGUN chain (tens of seconds of worker
  // work). Multiple consumers (Activity screen + PrivateActivityPositions) fire it
  // from effects, and its `useCallback` identity churns on every balance update,
  // so without this lock the effects stack N concurrent 3-chain sweeps on boot.
  const historyFetchInFlightRef = useRef(false)
  // The `${account}:${filter}` key of the sweep currently in flight. A request
  // arriving for a DIFFERENT key (e.g. the user switched the network filter
  // BNB→Ethereum mid-sweep) must not be dropped like a same-key duplicate —
  // that left the new selection permanently stale. It is remembered here and
  // re-fired once the in-flight sweep finishes.
  const historyFetchInFlightKeyRef = useRef<string | null>(null)
  const pendingHistorySweepRef = useRef(false)
  // Read-only mirror of `isBalanceFetching` so `refreshPrivateHistory` can check it
  // without listing it as a dependency. Keeping `isBalanceFetching` in the callback's
  // dep array re-created the callback on every fetch (true→false), which re-fired the
  // consumer effects and triggered another full sweep — a self-sustaining loop.
  const isBalanceFetchingRef = useRef(isBalanceFetching)
  // Mobile-as-webapp has no SW → no `swBalanceSummaries` ever lands → `isInitialPrivateSync`
  // below would stay true forever (its only "done" signal is a summary with `refreshedAt > 0`).
  // Track per-account in-page fetch completion so the loading hint clears after the first
  // successful `getBalance` round, including the legitimate "0 shielded balance" case.
  // Keys are `${accountAddr}:${chainId}` (see `inPageChainKey`). Per-chain, not
  // per-account, so a never-scanned chain still drives the syncing UI after a
  // sibling chain on the same account has completed.
  const [inPageFetchCompletedChainKeys, setInPageFetchCompletedChainKeys] = useState<Set<string>>(
    () => new Set()
  )

  useEffect(() => {
    isPrivateModeRef.current = isPrivateMode
  }, [isPrivateMode])

  useEffect(() => {
    isBalanceFetchingRef.current = isBalanceFetching
  }, [isBalanceFetching])

  // Mobile browsers can't scan all RAILGUN chains at once (OOM). On entering private
  // mode, clamp the dashboard network filter to a single chain: keep the current chain
  // if it's RAILGUN-supported (so coming from public stays on the same chain), else
  // fall back to BNB. The scan paths clamp too (resolvePrivateBalanceFilter), which
  // guards the first fetch before this dispatch round-trips; this persists the choice
  // so the network dropdown shows a concrete selection instead of "All Networks".
  useEffect(() => {
    if (!isMobileBrowser || !isPrivateMode) return
    const id = dashboardNetworkFilter == null ? null : Number(dashboardNetworkFilter)
    if (id != null && !Number.isNaN(id) && EXTENSION_PRIVATE_CHAIN_IDS.includes(id)) return
    dispatch({
      type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
      params: { dashboardNetworkFilter: BigInt(DEFAULT_MOBILE_PRIVATE_CHAIN_ID) }
    })
  }, [isPrivateMode, dashboardNetworkFilter, dispatch])

  // Load railgun wallets from storage, hydrated with the persisted balance summary
  // (totalBalanceUSD + balanceLastUpdatedAt). Phase 0.4 of RAILGUN_HYBRID_REWRITE_PLAN.md:
  // surviving a hard reload requires the balance metadata to live in chrome.storage, not just
  // in-memory state. Token balance maps are intentionally NOT persisted — only the aggregate
  // USD figure used by the dashboard.
  //
  // Phase 2.3 — `railgunBackgroundWallets` is the single source of truth for Railgun
  // identity rows AND the per-account aggregate `totalBalanceUSD` / `balanceLastUpdatedAt`
  // headline. The legacy `railgunWallets` and `railgunBalanceSummaryByAccount` keys are
  // migrated and removed by the SW controller on init. Token balance maps remain in-memory
  // only — UI's `fetchBalance` produces them on demand.
  const loadRailgunWallets = async () => {
    try {
      return (await storage.get('railgunBackgroundWallets', [])) as RailgunWalletData[]
    } catch (error) {
      console.error('Failed to load Railgun wallets:', error)
      return []
    }
  }

  // Load the persisted Railgun wallet identity list from storage independently of engine state.
  // The Private-mode toggle (and other identity-only checks like `hasRailgunWallet`) only need
  // accountAddr → railgunAddress mapping, which lives in storage and doesn't require a running
  // engine. Without this, the toggle would render disabled until the user enters private mode
  // (the only path that initializes the UI engine after the deferred-init change).
  useEffect(() => {
    if (!isUnlocked) return
    let cancelled = false
    void (async () => {
      const stored = await loadRailgunWallets()
      if (cancelled) return
      if (!persistedRailgunWalletsEqual(railgunWalletsRef.current, stored)) {
        // Merge — don't replace — so a transient empty/stale storage read can't wipe the
        // in-memory token maps + fetched headline. See reconcilePersistedRailgunWallets.
        setRailgunWallets((prev) => reconcilePersistedRailgunWallets(prev, stored))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isUnlocked])

  // Eager Railgun-wallet creation for seed-imported accounts.
  //
  // Engine init is deferred (only runs on entering private mode), and `handleLoadRailgunWallets`
  // — which actually creates the per-account Railgun wallet from the seed — is gated behind
  // `railgunEngineInitialized`. For a freshly imported seed account this would mean:
  //   no Railgun wallet → Private toggle disabled → user can't open Private → engine never inits
  //   → wallet never created. Break the chicken-and-egg by triggering engine init as soon as we
  // detect the current account has a seed-derived internal key but no persisted Railgun wallet.
  // `ensureRailgunEngine` is idempotent; `handleLoadRailgunWallets` will then run via its own
  // effect on `railgunEngineInitialized` and persist the wallet.
  useEffect(() => {
    // Mobile-web: do NOT eagerly boot the engine for seed accounts at unlock.
    // The web app should stay lightweight — the heavy RAILGUN engine/scan only
    // spins up when the user actually opts into private mode via the toggle (not
    // every user has a private wallet). The Private toggle is rendered in the
    // 'creatable' state (clickable) and triggers `ensureRailgunEngine` on click,
    // so the chicken-and-egg the eager init guarded against no longer applies on
    // this build. The offscreen/extension path keeps eager init unchanged.
    if (FEATURES.railgunWorkerIsolationEnabled) return
    if (!isUnlocked) return
    if (!account) return
    if (railgunEngineInitialized) return

    const accountKeys = keys.filter((k) =>
      account.associatedKeys.some((a) => a.toLowerCase() === k.addr.toLowerCase())
    )
    const hasSeedKey = accountKeys.some(
      (k) => k.type === 'internal' && typeof (k as any).meta?.fromSeedId === 'string'
    )
    if (!hasSeedKey) return

    const persistedHasWallet = railgunWalletsRef.current.some(
      (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
    )
    if (persistedHasWallet) return

    ensureRailgunEngine()
  }, [isUnlocked, account, keys, railgunEngineInitialized, ensureRailgunEngine])

  const retrieveSeedPhrase = useCallback(
    async (accountAddr: string): Promise<{ seed: string; seedPassphrase?: string }> => {
      const account = accounts.find((acc) => acc.addr.toLowerCase() === accountAddr.toLowerCase())
      if (!account) {
        throw new Error(`Account not found: ${accountAddr}`)
      }

      const accountKeys = keys.filter((key) => account.associatedKeys.includes(key.addr))

      const seedId = accountKeys.find((key) => key.type === 'internal' && key.meta?.fromSeedId)
        ?.meta?.fromSeedId

      if (!seedId) {
        throw new Error(`No seed ID found for account: ${accountAddr}`)
      }

      // Tier 2 keystore hardening: do NOT cache the plaintext seed. A session-long
      // cache keeps the mnemonic resident in the page heap for the whole unlock
      // session, where a page-context XSS / heap dump can read it. Each legacy
      // (worker-isolation-off) caller fetches a transient copy that drops out of
      // scope — and GC-eligible — as soon as the wallet instance is built. With
      // `railgunWorkerIsolationEnabled` (default) the seed never reaches the page
      // at all; this fallback only runs when that flag is off.
      // Return a Promise that resolves when the seed phrase is received
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          eventBus.removeEventListener('receiveOneTimeData', onReceiveOneTimeData)
          reject(new Error('Timeout waiting for seed phrase'))
        }, 10000) // 10 second timeout

        const onReceiveOneTimeData = (data: any) => {
          if (data.seed) {
            clearTimeout(timeout)
            eventBus.removeEventListener('receiveOneTimeData', onReceiveOneTimeData)

            const seedData = {
              seed: data.seed,
              seedPassphrase: data.seedPassphrase || undefined
            }

            resolve(seedData)
          }
        }

        eventBus.addEventListener('receiveOneTimeData', onReceiveOneTimeData)

        dispatch({
          type: 'KEYSTORE_CONTROLLER_SEND_SEED_TO_UI',
          params: { id: seedId }
        })
      })
    },
    [accounts, keys, dispatch]
  )

  // Keep refs in sync with callbacks
  useEffect(() => {
    retrieveSeedPhraseRef.current = retrieveSeedPhrase
  }, [retrieveSeedPhrase])

  // A1 keystore-isolation: resolve the keystore seedId for an account WITHOUT
  // fetching the seed to the page (mirrors the seedId lookup in
  // `retrieveSeedPhrase`, minus the `SEND_SEED_TO_UI` round-trip).
  const resolveSeedIdForAccount = useCallback(
    (accountAddr: string): string => {
      const acc = accounts.find((a) => a.addr.toLowerCase() === accountAddr.toLowerCase())
      if (!acc) throw new Error(`Account not found: ${accountAddr}`)
      const accountKeys = keys.filter((key) => acc.associatedKeys.includes(key.addr))
      const seedId = accountKeys.find((key) => key.type === 'internal' && key.meta?.fromSeedId)
        ?.meta?.fromSeedId
      if (!seedId) throw new Error(`No seed ID found for account: ${accountAddr}`)
      return seedId
    },
    [accounts, keys]
  )

  // A1 keystore-isolation: generic page → keystore worker → railgun worker RPC.
  // Generates a requestId, registers the waiter, and dispatches the worker-tier
  // pseudo-action (intercepted in `mainController.worker.ts` before controller
  // routing — not a typed controller Action). Only non-secret results return.
  const railgunWorkerRpc = useCallback(
    (method: string, params: any): Promise<any> => {
      const requestId = nextRailgunWalletRequestId()
      const resultPromise = awaitRailgunWalletResult(requestId, RAILGUN_WORKER_WALLET_TIMEOUT_MS)
      dispatch({ type: 'RAILGUN_WORKER_RPC', params: { requestId, method, params } } as any)
      return resultPromise
    },
    [dispatch]
  )

  // A1 keystore-isolation: create (≡ deterministically load) the railgun wallet
  // for a seed account via the worker. The page sends only the seedId; the
  // keystore worker yields the mnemonic, the nested railgun worker creates the
  // wallet, and the page receives only the public identity (no seed, no
  // encryptionKey). Returns the same shape as `createWalletInstanceFromSeed`.
  const createWalletInstanceViaWorker = useCallback(
    async (
      accountAddr: string
    ): Promise<{
      wallet: WorkerRailgunWalletProxy
      railgunAddress: string
      viewingKey: string
    }> => {
      const seedId = resolveSeedIdForAccount(accountAddr)
      const info: RailgunWalletPublicInfo = await railgunWorkerRpc('createWallet', {
        seedId,
        accountIndex: RAILGUN_DEFAULT_ACCOUNT_INDEX
      })
      return {
        wallet: new WorkerRailgunWalletProxy(info, railgunWorkerRpc),
        railgunAddress: info.railgunAddress,
        viewingKey: info.viewingKey
      }
    },
    [resolveSeedIdForAccount, railgunWorkerRpc]
  )

  // POI-required is an engine query that needs loaded providers. Under worker
  // isolation the engine + providers live in the nested worker, so calling
  // `getPOIRequiredForNetworkForChain` on the page throws "Provider not yet
  // loaded" — route it through the worker (non-secret). Defined here (after
  // `railgunWorkerRpc`) so the worker branch can reach it.
  const triggerPOISync = useCallback(() => {
    const requiredPromise = FEATURES.railgunWorkerIsolationEnabled
      ? railgunWorkerRpc('poiRequiredForNetwork', { chainId: 42161 })
      : getPOIRequiredForNetworkForChain({ chainId: 42161 })
    void requiredPromise.then((required) => {
      if (!required) return

      emitRailgunEvent('poiProofProgress', {
        status: ExtendedPOIProofEventStatus.NewTransactionLoading,
        progress: 0,
        totalCount: 0,
        index: 0,
        errMessage: undefined,
        txid: '',
        listKey: '',
        railgunTxid: ''
      } as unknown as POIProofProgressEvent)
    })
  }, [railgunWorkerRpc])

  // Phase 0.2 spend-lock: throw before any proof generation or tx execution if the local
  // UTXO merkle root disagrees with on-chain rootHistory for the current account.
  // Shields are intentionally not blocked — only spends from the poisoned cache.
  // M8 of RAILGUN_BALANCE_SYNC_REFACTOR_PLAN_VI.md — signature takes `chainId`. Under the
  // per-chain flag, block only that chain; other chains stay spendable. Flag off keeps
  // legacy account-wide block.
  const swAccountSnapshotsRef = useRef<any>(undefined)
  const assertCacheNotPoisoned = useCallback(
    (chainId: number) => {
      if (!account?.addr) return
      if (FEATURES.railgunPerChainSync) {
        const snap = swAccountSnapshotsRef.current?.[account.addr.toLowerCase()]
        const chain = snap?.chains?.[String(chainId) as unknown as number]
        if (chain?.cachePoisoned) {
          throw new Error(
            `Spend blocked on chain ${chainId}: local Railgun balance cache is out of sync with the on-chain merkle root. Please wait for the next sync to verify.`
          )
        }
        return
      }
      const wallet = railgunWalletsRef.current.find(
        (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
      )
      if (wallet?.cachePoisoned) {
        const networks = wallet.cachePoisonedNetworks?.length
          ? ` (${wallet.cachePoisonedNetworks.join(', ')})`
          : ''
        throw new Error(
          `Spend blocked: local Railgun balance cache is out of sync with the on-chain merkle root${networks}. Please wait for the next sync to verify.`
        )
      }
    },
    [account]
  )

  const handleLoadRailgunWallets = useCallback(async () => {
    if (!railgunEngineInitialized) return
    if (!isUnlocked) return

    // A1 keystore-isolation: when on, the railgun wallet is created/loaded inside
    // the worker (the page never pulls the seed or holds the encryptionKey).
    const useWorker = FEATURES.railgunWorkerIsolationEnabled

    setIsLoading(true)
    try {
      const storedWallets = await loadRailgunWallets()
      if (!persistedRailgunWalletsEqual(railgunWalletsRef.current, storedWallets)) {
        // Merge — don't replace — so a transient empty/stale storage read can't wipe the
        // in-memory token maps + fetched headline. See reconcilePersistedRailgunWallets.
        setRailgunWallets((prev) => reconcilePersistedRailgunWallets(prev, storedWallets))
      }

      // Find accounts without railgun wallets
      const accountsWithoutRailgunWallets = accounts.filter((acc) => {
        const hasRailgunWallet = storedWallets.some(
          (wallet) => wallet.accountAddr.toLowerCase() === acc.addr.toLowerCase()
        )
        return !hasRailgunWallet
      })

      // If no accounts without railgun wallets, ensure current account has wallet instance
      if (accountsWithoutRailgunWallets.length === 0) {
        if (!account) {
          setIsLoading(false)
          return
        }
        let railgunWallet: WalletInstance | null = null
        if (useWorker) {
          // Worker re-creates deterministically (≡ load) from the seed it holds;
          // the page never sees the seed/encryptionKey.
          try {
            railgunWallet = (await createWalletInstanceViaWorker(account.addr)).wallet
          } catch (err) {
            console.error('[Railgun] worker wallet load failed:', err)
            setIsLoading(false)
            return
          }
          const normalizedAddr = account.addr.toLowerCase()
          setWalletInstances((prev) => {
            const next = new Map(prev)
            next.set(normalizedAddr, railgunWallet!)
            return next
          })
          setFirstFetchDone(true)
          setIsLoading(false)
          return
        }
        try {
          railgunWallet = await loadOrCreateWalletInstanceForAccount(
            account,
            storedWallets,
            retrieveSeedPhrase
          )
        } catch (err) {
          // Partial-state recovery: engine DB lost the primary wallet record
          // for this account's persisted walletId. Per-chain sub-records may
          // still exist; walletId/encryptionKey are deterministic from the
          // seed, so re-creating produces the same id and rejoins those
          // sub-records. Only attempt for accounts with an internal key.
          const accountKeys = keys.filter((key) => account.associatedKeys.includes(key.addr))
          const hasSeedKey = accountKeys.some(
            (key) => key.type === 'internal' && key.meta?.fromSeedId
          )
          if (!isWalletPrimaryRecordMissingError(err) || !hasSeedKey) throw err
          console.warn('[Railgun] partial-state recovered (recreate from seed)', account.addr)
          const seedPhrase = await retrieveSeedPhrase(account.addr)
          const created = await createWalletInstanceFromSeed(seedPhrase.seed, seedPhrase.seedPassphrase)
          railgunWallet = created.wallet
          const recreatedRow: RailgunWalletData = {
            accountAddr: account.addr,
            railgunAddress: created.railgunAddress,
            viewingKey: created.viewingKey,
            createdAt: Date.now(),
            walletId: created.wallet.getWalletId(),
            encryptionKey: created.wallet.getEncryptionKey()
          }
          setRailgunWallets((prev) => {
            const lower = account.addr.toLowerCase()
            const others = prev.filter((w) => w.accountAddr.toLowerCase() !== lower)
            return [...others, recreatedRow]
          })
          dispatch({
            type: 'RAILGUN_BACKGROUND_CONTROLLER_PERSIST_WALLET',
            params: {
              accountAddr: recreatedRow.accountAddr,
              railgunAddress: recreatedRow.railgunAddress,
              viewingKey: recreatedRow.viewingKey,
              createdAt: recreatedRow.createdAt,
              walletId: recreatedRow.walletId!,
              encryptionKey: recreatedRow.encryptionKey!
            }
          })
        }
        const normalizedAddr = account.addr.toLowerCase()
        setWalletInstances((prev) => {
          const next = new Map(prev)
          next.set(normalizedAddr, railgunWallet!)
          return next
        })
        setFirstFetchDone(true)
        setIsLoading(false)
        return
      }

      // Only create for accounts whose internal key is seed-derived. Private-key-imported
      // accounts have type='internal' but no meta.fromSeedId, so retrieveSeedPhrase would
      // throw and abort the loop, starving sibling seed-backed accounts (and blocking
      // engine init, which waits on wallet load).
      const accountsToCreate = accountsWithoutRailgunWallets.filter((acc) => {
        const accountKeys = keys.filter((key) => acc.associatedKeys.includes(key.addr))
        return accountKeys.some((key) => key.type === 'internal' && key.meta?.fromSeedId)
      })

      // Hardware/view-only may be the only accounts missing Railgun on disk — still unblock UI and
      // load an instance for the selected account when it already has persisted Railgun keys.
      if (accountsToCreate.length === 0) {
        if (account) {
          const fromStorage = await loadWalletInstanceFromStorageOnly(account, storedWallets)
          if (fromStorage) {
            const normalizedAddr = account.addr.toLowerCase()
            setWalletInstances((prev) => {
              const next = new Map(prev)
              next.set(normalizedAddr, fromStorage)
              return next
            })
          }
          setFirstFetchDone(true)
        }
        setIsLoading(false)
        return
      }

      // Retrieve seed phrase for each account. SKIPPED under A1: the page must
      // not pull the seed — the worker derives it from the keystore on demand.
      const retrievedSeedPhrases: Array<{
        accountAddr: string
        seedPhrase: { seed: string; seedPassphrase?: string }
      }> = []
      if (!useWorker) {
        for (const acc of accountsToCreate) {
          try {
            const seedPhrase = await retrieveSeedPhrase(acc.addr)
            retrievedSeedPhrases.push({ accountAddr: acc.addr, seedPhrase })
          } catch (error) {
            console.error(`Failed to retrieve seed phrase for account ${acc.addr}:`, error)
          }
        }
      }

      const tempRailgunWallets: RailgunWalletData[] = []

      for (const account of accountsToCreate) {
        // A1: create via the worker (no seed on the page); otherwise create
        // in-page from the retrieved seed phrase.
        const created = useWorker
          ? await createWalletInstanceViaWorker(account.addr).catch((error) => {
              console.error(`[Railgun] worker create failed for ${account.addr}:`, error)
              return null
            })
          : await (async () => {
              const seedPhrase = retrievedSeedPhrases.find(
                (s) => s.accountAddr === account.addr
              )?.seedPhrase
              return seedPhrase ? createWalletInstanceFromSeed(seedPhrase.seed, seedPhrase.seedPassphrase) : null
            })()

        if (created) {
          const normalizedAddr = account.addr.toLowerCase()
          setWalletInstances((prev) => {
            const next = new Map(prev)
            next.set(normalizedAddr, created.wallet)
            return next
          })

          const walletData: RailgunWalletData = {
            accountAddr: account.addr,
            railgunAddress: created.railgunAddress,
            viewingKey: created.viewingKey,
            createdAt: Date.now(),
            walletId: created.wallet.getWalletId(),
            // A1: never persist an encryptionKey to page storage — the worker
            // re-derives it from the seed each load (§13.1.6).
            ...(useWorker ? {} : { encryptionKey: created.wallet.getEncryptionKey() })
          }

          tempRailgunWallets.push(walletData)
        }
      }

      // Append newly created wallets only; keep existing in-memory rows (balances) untouched.
      if (tempRailgunWallets.length > 0) {
        setRailgunWallets((prev) => {
          const prevAddrs = new Set(prev.map((w) => w.accountAddr.toLowerCase()))
          const toAppend = tempRailgunWallets.filter(
            (t) => !prevAddrs.has(t.accountAddr.toLowerCase())
          )
          return toAppend.length > 0 ? [...prev, ...toAppend] : prev
        })
      }

      // Persist via SW so the unified `railgunBackgroundWallets` store is the sole writer.
      // (Phase 2.3 — UI no longer touches the legacy `railgunWallets` key.)
      for (const row of tempRailgunWallets) {
        if (!row.walletId || !row.encryptionKey) continue
        dispatch({
          type: 'RAILGUN_BACKGROUND_CONTROLLER_PERSIST_WALLET',
          params: {
            accountAddr: row.accountAddr,
            railgunAddress: row.railgunAddress,
            viewingKey: row.viewingKey,
            createdAt: row.createdAt,
            walletId: row.walletId,
            encryptionKey: row.encryptionKey
          }
        })
      }

      // Worker build (mobile-web) has a stub SW controller and no page-side encryptionKey, so
      // the dispatch above is a no-op there. Persist the public identity directly so the
      // accountAddr→railgunAddress mapping survives into cold sessions and the Shield/Unshield
      // recipient picker is populated without first entering private mode.
      if (useWorker) {
        void persistRailgunIdentityRowsLocally(tempRailgunWallets)
      }

      setFirstFetchDone(true)
    } catch (error) {
      console.error('Failed to load/create railgun wallets:', error)
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accounts,
    keys,
    isUnlocked,
    railgunEngineInitialized,
    loadRailgunWallets,
    retrieveSeedPhrase,
    createWalletInstanceViaWorker,
    walletInstances,
    account
  ])

  const handleLoadRailgunWalletsRef = useRef(handleLoadRailgunWallets)
  useEffect(() => {
    handleLoadRailgunWalletsRef.current = handleLoadRailgunWallets
  }, [handleLoadRailgunWallets])

  // Re-run when the Railgun engine becomes ready, when accounts change (e.g. new wallet added), or when
  // keys change so internal keys exist for a new account. Ref avoids re-running when walletInstances updates.
  useEffect(() => {
    if (!railgunEngineInitialized) return
    void handleLoadRailgunWalletsRef.current()
  }, [railgunEngineInitialized, accounts, keys])

  // A1 keystore-isolation: an auto-lock terminates the nested railgun worker
  // (engine + loaded wallets wiped). `railgunEngineInitialized` is sticky, so the
  // effect above won't re-fire on unlock. Re-load the wallet into the freshly
  // respawned worker when the keystore transitions locked→unlocked, otherwise the
  // worker has no wallet and balance/spend RPCs fail with "wallet not loaded".
  const prevUnlockedRef = useRef(isUnlocked)
  useEffect(() => {
    const wasUnlocked = prevUnlockedRef.current
    prevUnlockedRef.current = isUnlocked
    if (!FEATURES.railgunWorkerIsolationEnabled) return
    if (wasUnlocked || !isUnlocked) return // only on locked → unlocked
    if (!railgunEngineInitialized) return
    void handleLoadRailgunWalletsRef.current()
  }, [isUnlocked, railgunEngineInitialized])

  // Listen for storage changes
  useEffect(() => {
    // `browser` is null on the mobile-as-webapp build (no webextension-polyfill).
    // No `chrome.storage` listener is needed there — the in-process ControllerStore
    // is the single source of truth.
    if (!browser?.storage?.onChanged) return

    const handleStorageChange = (
      changes: { [key: string]: chrome.storage.StorageChange },
      areaName: string
    ) => {
      if (areaName !== 'local') return
      // SW-owned identity store changed — reload through `loadRailgunWallets()` so the UI
      // picks up rows the SW just persisted (e.g. fresh wallet from #loadWalletsIntoMemory,
      // or a row dispatched via `RAILGUN_BACKGROUND_CONTROLLER_PERSIST_WALLET`).
      if (!changes.railgunBackgroundWallets) return
      void (async () => {
        const merged = await loadRailgunWallets()
        setRailgunWallets((prev) => {
          if (persistedRailgunWalletsEqual(prev, merged)) return prev
          console.log('[RailgunUI total←storage-onChanged-replace]', {
            prevTotals: prev.map((w) => [w.accountAddr.slice(0, 10), w.totalBalanceUSD] as const),
            mergedTotals: merged.map(
              (w) => [w.accountAddr.slice(0, 10), w.totalBalanceUSD] as const
            ),
            prevTokenMaps: prev.map(
              (w) => [w.accountAddr.slice(0, 10), w.balanceTokenMap?.size ?? 0] as const
            )
          })
          return merged
        })
      })()
    }

    browser.storage.onChanged.addListener(handleStorageChange)

    return () => {
      browser.storage.onChanged.removeListener(handleStorageChange)
    }
  }, [])

  // Get current railgun wallet for selected account
  const currentRailgunWallet = useMemo(() => {
    if (!account?.addr) return null
    const acctLower = account.addr.toLowerCase()
    const found =
      railgunWallets.find((wallet) => wallet.accountAddr.toLowerCase() === acctLower) || null
    console.log('[RailgunUI currentRailgunWallet]', {
      accountAddr: account.addr.slice(0, 10),
      walletCount: railgunWallets.length,
      walletAddrs: railgunWallets.map((w) => w.accountAddr.slice(0, 10)),
      found: !!found,
      balanceTokenMapSize: found?.balanceTokenMap?.size ?? 0,
      balanceTokenMapEntries: found?.balanceTokenMap
        ? Array.from(found.balanceTokenMap.entries()).map(
            ([cid, arr]) => [cid, arr.length] as const
          )
        : null
    })
    return found
  }, [account?.addr, railgunWallets])

  // Filter railgun wallets to only include accounts that exist
  const allRailgunWallets = useMemo(() => {
    if (!accounts.length) return railgunWallets

    const accountAddrs = new Set(accounts.map((acc) => acc.addr))
    return railgunWallets.filter((wallet) => accountAddrs.has(wallet.accountAddr))
  }, [accounts, railgunWallets])

  // Phase 2.3 (Slice C) — SW controller-state is the single source of truth for both the
  // headline summary and the per-token balance maps. UI no longer issues `WALLET_GET_BALANCE`
  // against the offscreen; the SW's `#refreshAllBalances` runs the full pipeline (engine
  // fetch → merkleRoot verify → pricing) and broadcasts results via `useControllerState`.
  // Live per-chain progress still flows through the `balanceUpdate` event listener below
  // (offscreen → UI port), keeping the dashboard responsive between full syncs.
  const railgunBgState = useControllerState('RailgunBackgroundController')

  // SW's `engineInitialized` flips false on idle-stop and back to true after recovery —
  // unlike the UI-side `railgunEngineInitialized` flag which is sticky once seen-up.
  // The freshly recreated offscreen has no in-memory wallet state (the SDK's wallet map
  // lives in offscreen process memory, not IndexedDB), so any UI rpc that needs a loaded
  // wallet (gas estimate, proof gen, populate) will fail with "Wallet not found" until we
  // reload. Re-fire wallet loading on every false→true transition of the SW signal.
  const swEngineReady = !!(railgunBgState as any)?.engineInitialized
  const prevSwEngineReadyRef = useRef(swEngineReady)
  useEffect(() => {
    const prev = prevSwEngineReadyRef.current
    prevSwEngineReadyRef.current = swEngineReady
    if (!prev && swEngineReady && railgunEngineInitialized) {
      void handleLoadRailgunWalletsRef.current()
    }
  }, [swEngineReady, railgunEngineInitialized])

  const swBalanceSummaries = (railgunBgState as any)?.balanceSummaries as
    | Array<{
        accountAddr: string
        totalBalanceUSD: number
        refreshedAt: number
        error?: string
        cachePoisoned?: boolean
      }>
    | undefined
  useEffect(() => {
    if (FEATURES.railgunPerChainSync) return // M2: snapshot effect owns adoption
    if (!swBalanceSummaries?.length) return
    setRailgunWallets((prev) => {
      let changed = false
      const next = prev.map((w) => {
        const summary = swBalanceSummaries.find(
          (s) => s.accountAddr.toLowerCase() === w.accountAddr.toLowerCase()
        )
        if (!summary) return w
        // SW emits a $0 summary with `error` set when its in-memory wallet map is briefly
        // empty for the selected account (e.g. a transient window during cold-start before
        // `loadWalletsIntoMemory` populates instances). Adopting that $0 would clobber a
        // correct UI value, so skip error rows entirely — the next clean SW emit will
        // catch the UI back up.
        if (summary.error) return w
        // Only adopt the SW value if it's strictly newer than what we have, or if the
        // local row carries no data of its own (cold UI start). This avoids fighting an
        // in-flight UI fetch that produced a richer per-token map alongside its own
        // totalBalanceUSD.
        const localTs = w.balanceLastUpdatedAt ?? 0
        const localTotal = w.totalBalanceUSD ?? 0
        const swIsNewer = summary.refreshedAt > localTs
        const localIsEmpty = localTotal === 0 && !w.balanceTokenMap?.size
        if (!swIsNewer && !localIsEmpty) return w
        // Defense in depth: never downgrade a known-good positive headline to $0 from the
        // SW mirror when the local row still has tokens. Phase 2.3 (Slice C) — SW skips
        // broadcasting summaries flagged with `error`, but a transient untrustworthy total
        // (pricing-incomplete) can still arrive without `error`. Preserving the local total
        // bridges that gap until the next clean SW emit.
        if (summary.totalBalanceUSD === 0 && localTotal > 0 && (w.balanceTokenMap?.size ?? 0) > 0) {
          return w
        }
        if (
          w.totalBalanceUSD === summary.totalBalanceUSD &&
          w.balanceLastUpdatedAt === summary.refreshedAt &&
          (w.cachePoisoned ?? false) === (summary.cachePoisoned ?? false)
        ) {
          return w
        }
        changed = true
        if (w.totalBalanceUSD !== summary.totalBalanceUSD) {
          console.log('[RailgunUI total←SW-mirror]', {
            account: w.accountAddr.slice(0, 10),
            from: w.totalBalanceUSD,
            to: summary.totalBalanceUSD,
            tokenMapSize: w.balanceTokenMap?.size ?? 0,
            swIsNewer,
            localIsEmpty
          })
        }
        return {
          ...w,
          totalBalanceUSD: summary.totalBalanceUSD,
          balanceLastUpdatedAt: summary.refreshedAt,
          cachePoisoned: summary.cachePoisoned ?? w.cachePoisoned
        }
      })
      return changed ? next : prev
    })
  }, [swBalanceSummaries])

  // Phase 2.3 (Slice C) — mirror the SW's per-token balance maps into UI wallet rows.
  // Replaces the UI's direct `WALLET_GET_BALANCE` RPC. The SW serializes Map<chainId, …>
  // → Record<string, …> and bigint → string at the controller-state boundary; we rehydrate
  // back into `RailgunTokenBalanceMap` here.
  type SerializedBalance = { tokenAddress: string; amount: string }
  type SerializedAccountMaps = {
    balance: Record<string, SerializedBalance[]>
    pending: Record<string, SerializedBalance[]>
    missingInternal: Record<string, SerializedBalance[]>
    missingExternal: Record<string, SerializedBalance[]>
    prices: Record<string, number>
    refreshedAt: number
  }
  const swBalanceMapsByAccount = (railgunBgState as any)?.balanceMapsByAccount as
    | Record<string, SerializedAccountMaps>
    | undefined
  useEffect(() => {
    if (FEATURES.railgunPerChainSync) return // M2: snapshot effect owns adoption
    if (!swBalanceMapsByAccount) return
    const deserialize = (m: Record<string, SerializedBalance[]>): RailgunTokenBalanceMap => {
      const out: RailgunTokenBalanceMap = new Map()
      for (const [chainIdStr, balances] of Object.entries(m)) {
        out.set(
          Number(chainIdStr),
          balances.map((b) => ({ tokenAddress: b.tokenAddress, amount: BigInt(b.amount) }))
        )
      }
      return out
    }
    setRailgunWallets((prev) => {
      let changed = false
      const next = prev.map((w) => {
        const swMaps = swBalanceMapsByAccount[w.accountAddr.toLowerCase()]
        if (!swMaps) return w
        const balanceTokenMap = deserialize(swMaps.balance)
        const pendingBalanceTokenMap = deserialize(swMaps.pending)
        const missingInternalPOIBalanceTokenMap = deserialize(swMaps.missingInternal)
        const missingExternalPOIBalanceTokenMap = deserialize(swMaps.missingExternal)
        // Merge prices preserving prev keys: per-chain price fetches in the SW
        // (`fetchTokenPricesForBalanceMap`) can swallow per-chain network errors and return a
        // map missing a whole chain's entries. Replacing the map wholesale then wipes valid
        // prev prices and the UI shows `$-` even though the headline (which uses the SW's
        // own getTotalBalanceUSD computed BEFORE the wipe is observed here) is correct.
        const incomingPriceEntries = Object.entries(swMaps.prices ?? {})
        const prevPriceMap = w.balanceTokenPriceMap ?? new Map<string, number>()
        const balanceTokenPriceMap = new Map<string, number>(prevPriceMap)
        for (const [k, v] of incomingPriceEntries) balanceTokenPriceMap.set(k, v)
        // eslint-disable-next-line no-console
        console.log('[RailgunUI] price-map merge', {
          prevSize: prevPriceMap.size,
          incomingSize: incomingPriceEntries.length,
          mergedSize: balanceTokenPriceMap.size,
          prevChains: [...new Set(Array.from(prevPriceMap.keys()).map((k) => k.split('-')[0]))],
          incomingChains: [...new Set(incomingPriceEntries.map(([k]) => k.split('-')[0]))]
        })
        changed = true
        return {
          ...w,
          balanceTokenPriceMap,
          balanceTokenMap: mergeRailgunBalanceMaps(w.balanceTokenMap, balanceTokenMap),
          pendingBalanceTokenMap: mergeRailgunBalanceMaps(
            w.pendingBalanceTokenMap,
            pendingBalanceTokenMap
          ),
          missingInternalPOIBalanceTokenMap: mergeRailgunBalanceMaps(
            w.missingInternalPOIBalanceTokenMap,
            missingInternalPOIBalanceTokenMap
          ),
          missingExternalPOIBalanceTokenMap: mergeRailgunBalanceMaps(
            w.missingExternalPOIBalanceTokenMap,
            missingExternalPOIBalanceTokenMap
          )
        }
      })
      return changed ? next : prev
    })
  }, [swBalanceMapsByAccount, railgunWallets.length])

  // M2 of RAILGUN_BALANCE_SYNC_REFACTOR_PLAN_VI.md — single adoption effect for the SW's
  // atomic `accountSnapshots`. When `FEATURES.railgunPerChainSync` is on, this REPLACES the
  // two legacy mirror effects above (both early-return). When off, this effect is inert and
  // the legacy mirrors run as before — that's the rollback path.
  type SwAccountSnapshot = {
    schemaVersion: number
    accountAddr: string
    snapshotId: number
    updatedAt: number
    chains: Record<
      string,
      {
        chainId: number
        balance: Array<{ tokenAddress: string; amount: string }>
        pending: Array<{ tokenAddress: string; amount: string }>
        missingInternal: Array<{ tokenAddress: string; amount: string }>
        missingExternal: Array<{ tokenAddress: string; amount: string }>
        prices: Record<string, number>
        cachePoisoned: boolean
        lastSyncedAt: number
      }
    >
    requestedTotalBalanceUSD: number
    knownTotalBalanceUSD: number
    overallStatus: string
  }
  const swAccountSnapshots = (railgunBgState as any)?.accountSnapshots as
    | Record<string, SwAccountSnapshot>
    | undefined
  useEffect(() => {
    if (!FEATURES.railgunPerChainSync) return
    if (!swAccountSnapshots) return
    setRailgunWallets((prev) => {
      let changed = false
      const next = prev.map((w) => {
        const snap = swAccountSnapshots[w.accountAddr.toLowerCase()]
        if (!snap) return w

        const balanceTokenMap: RailgunTokenBalanceMap = new Map()
        const pendingBalanceTokenMap: RailgunTokenBalanceMap = new Map()
        const missingInternalPOIBalanceTokenMap: RailgunTokenBalanceMap = new Map()
        const missingExternalPOIBalanceTokenMap: RailgunTokenBalanceMap = new Map()
        const balanceTokenPriceMap = new Map<string, number>()
        let anyCachePoisoned = false
        for (const chain of Object.values(snap.chains)) {
          const toEntries = (arr: Array<{ tokenAddress: string; amount: string }>) =>
            arr.map((b) => ({ tokenAddress: b.tokenAddress, amount: BigInt(b.amount) }))
          balanceTokenMap.set(chain.chainId, toEntries(chain.balance))
          pendingBalanceTokenMap.set(chain.chainId, toEntries(chain.pending))
          missingInternalPOIBalanceTokenMap.set(chain.chainId, toEntries(chain.missingInternal))
          missingExternalPOIBalanceTokenMap.set(chain.chainId, toEntries(chain.missingExternal))
          for (const [k, v] of Object.entries(chain.prices ?? {})) {
            balanceTokenPriceMap.set(k, v)
          }
          if (chain.cachePoisoned) anyCachePoisoned = true
        }

        // Prefer `knownTotalBalanceUSD` so stale-but-real chains keep contributing instead
        // of dropping to $0 mid-resync. SW already excludes poisoned chains from this total.
        //
        // Empty-over-positive guard (doc §7): a re-published or early snapshot can carry
        // `knownTotalBalanceUSD === 0` while its Spendable/ShieldPending maps still hold
        // positive amounts — the $0 is a not-yet-priced artifact (prices land after the
        // bucket maps), not a real drain. Observed as the 0.109 → 0 churn when snapshot #1
        // was re-applied over the live balanceUpdate total. Treat that as untrustworthy and
        // keep the previous total; a real drain leaves the maps empty too, so we still let a
        // genuine 0 through. The bucket maps themselves are authoritative and always applied.
        const snapshotTotal = snap.knownTotalBalanceUSD
        const hasPositiveSpendable = [balanceTokenMap, pendingBalanceTokenMap].some((m) =>
          Array.from(m.values()).some((arr) => arr.some((b) => b.amount > 0n))
        )
        const emptyOverPositive =
          !snapshotTotal &&
          typeof w.totalBalanceUSD === 'number' &&
          w.totalBalanceUSD > 0 &&
          hasPositiveSpendable
        const totalBalanceUSD = emptyOverPositive ? w.totalBalanceUSD : snapshotTotal
        const balanceLastUpdatedAt = emptyOverPositive ? w.balanceLastUpdatedAt : snap.updatedAt

        if (
          w.totalBalanceUSD === totalBalanceUSD &&
          w.balanceLastUpdatedAt === balanceLastUpdatedAt &&
          (w.cachePoisoned ?? false) === anyCachePoisoned
        ) {
          return w
        }
        changed = true
        // TEMP ZERODIAG: snapshot effect overwrites bucket maps unconditionally.
        // Log spendable sizes incoming(snapshot) vs prev(displayed) to catch an
        // empty snapshot zeroing a good in-page balance.
        {
          const sz = (mp: RailgunTokenBalanceMap) =>
            JSON.stringify(Array.from(mp.entries()).map(([c, a]) => [c, a.length]))
          // eslint-disable-next-line no-console
          console.log(
            '[ZERODIAG snap] incomingSpend=' +
              sz(balanceTokenMap) +
              ' prevSpend=' +
              sz(w.balanceTokenMap ?? new Map()) +
              ' snapTotal=' +
              snapshotTotal +
              ' appliedTotal=' +
              totalBalanceUSD +
              ' emptyOverPositive=' +
              emptyOverPositive +
              ' status=' +
              snap.overallStatus
          )
        }
        if (w.totalBalanceUSD !== totalBalanceUSD) {
          console.log('[RailgunUI total←snapshot]', {
            account: w.accountAddr.slice(0, 10),
            from: w.totalBalanceUSD,
            to: totalBalanceUSD,
            snapshotId: snap.snapshotId,
            overallStatus: snap.overallStatus
          })
        }
        return {
          ...w,
          totalBalanceUSD,
          balanceLastUpdatedAt,
          cachePoisoned: anyCachePoisoned,
          balanceTokenMap,
          pendingBalanceTokenMap,
          missingInternalPOIBalanceTokenMap,
          missingExternalPOIBalanceTokenMap,
          balanceTokenPriceMap
        }
      })
      return changed ? next : prev
    })
  }, [swAccountSnapshots])

  useEffect(() => {
    swAccountSnapshotsRef.current = swAccountSnapshots
  }, [swAccountSnapshots])

  // Per-chain UTXO merkletree scan status. When all queried chains report `Complete`, the
  // engine has observed every commitment up to chainHead — an empty `getBalance` is then
  // authoritative (truthful drain). Otherwise the engine simply hasn't seen our UTXOs yet.
  //
  // Auto-refetch: when a chain transitions to `Complete`, trigger `fetchBalance` so the
  // dashboard picks up that chain's tokens without requiring a manual reload. Without this
  // the user sees BNB tokens immediately but Ethereum/Arbitrum tokens never appear because
  // those chains finish scanning AFTER `fetchBalance` already resolved.
  const chainScanStatusRef = useRef<Map<number, MerkletreeScanStatus>>(new Map())
  const autoRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastAutoRefetchAtRef = useRef<number>(0)
  // Chains already auto-refetched since the last user-initiated fetch (chain
  // switch / account switch). Without this, the auto-refetch becomes an infinite
  // re-scan loop on the SELECTED chain: `fetchBalance` re-runs `refreshBalances`,
  // the worker re-scans (~9s, > the 5s cooldown), emits `utxoScan:Complete` again,
  // which re-arms the Complete transition and fires another auto-refetch — forever
  // (observed: one chain re-scanned 14× in 35s, dashboard stuck "100%"). Gate to
  // ONE auto-refetch per chain per selection; the set is cleared on a genuine
  // chain/account switch so a fresh selection gets its single late-token refetch.
  const autoRefetchedChainsRef = useRef<Set<number>>(new Set())
  useEffect(() => {
    // M6: the SW owns scan-complete auto-refetch under per-chain-sync — but ONLY on the
    // extension target. On mobile-web there is no SW (the engine runs in-page), so nothing
    // re-fetches prices after a chain's scan/relabel lands its balances later. Without this
    // the first (early) in-page fetchBalance prices an empty BNB map, then late balanceUpdate
    // events fill in the token amounts with no price refetch → rows show "$-" and the headline
    // stays "$0.00". Keep the auto-refetch alive when there's no SW to own it.
    if (FEATURES.railgunPerChainSync && isExtension) return
    const AUTO_REFETCH_DEBOUNCE_MS = 800
    const AUTO_REFETCH_COOLDOWN_MS = 5_000
    const handle = (event: MerkletreeScanUpdateEvent) => {
      const prev = chainScanStatusRef.current.get(event.chain.id)
      chainScanStatusRef.current.set(event.chain.id, event.scanStatus)
      const transitionedToComplete =
        prev !== MerkletreeScanStatus.Complete && event.scanStatus === MerkletreeScanStatus.Complete
      if (!transitionedToComplete) return
      if (!isPrivateModeRef.current) return
      // Fire at most once per chain per selection. The fetchBalance this triggers
      // re-scans the chain and re-emits Complete; without this guard that re-arms
      // the transition above and loops indefinitely. Cleared on chain/account
      // switch (see the switch effects below).
      if (autoRefetchedChainsRef.current.has(event.chain.id)) return
      const now = Date.now()
      if (now - lastAutoRefetchAtRef.current < AUTO_REFETCH_COOLDOWN_MS) return
      // Debounce: coalesce multiple chains completing within a short window into one refetch.
      if (autoRefetchTimerRef.current) clearTimeout(autoRefetchTimerRef.current)
      autoRefetchTimerRef.current = setTimeout(() => {
        autoRefetchTimerRef.current = null
        if (!isPrivateModeRef.current) return
        const fetchFn = fetchBalanceRef.current
        if (!fetchFn) return
        autoRefetchedChainsRef.current.add(event.chain.id)
        lastAutoRefetchAtRef.current = Date.now()
        console.log(
          `[RailgunUI] chain ${event.chain.id} scan reached Complete — auto-refetching balance`
        )
        void fetchFn()
      }, AUTO_REFETCH_DEBOUNCE_MS)
    }
    railgunEmitter.on('utxoScanUpdate', handle)
    return () => {
      railgunEmitter.off('utxoScanUpdate', handle)
      if (autoRefetchTimerRef.current) {
        clearTimeout(autoRefetchTimerRef.current)
        autoRefetchTimerRef.current = null
      }
    }
  }, [])

  // TEMP ZERODIAG (ETH-loads-0 hunt): surface every UTXO merkletree scan update onto the
  // PAGE console. The SDK's scan callback runs inside railgun.worker and only logs under
  // RAILGUN_DEBUG, so on a real iPhone (no CDP, can't read worker target) there's no way to
  // tell "ETH still cold-scanning" (expected: minutes) from "ETH scan stalled/never started"
  // (bug). This logs chain + status + progress where Safari Web Inspector can read it.
  // Remove once the ETH zero-balance question is settled.
  useEffect(() => {
    const mkLog = (kind: 'utxo' | 'txid') => (event: MerkletreeScanUpdateEvent) => {
      // eslint-disable-next-line no-console
      console.log(`[ZERODIAG scan ${kind}]`, {
        chain: event.chain.id,
        status: event.scanStatus,
        progress: Math.round((event.progress ?? 0) * 100) / 100
      })
      // `Incomplete` = the engine GAVE UP on this chain's scan (errored / lost the
      // provider) — this is the bug signature for "progress froze then 0 balance",
      // as opposed to a healthy run that ends on `Complete`.
      if (event.scanStatus === MerkletreeScanStatus.Incomplete) {
        // eslint-disable-next-line no-console
        console.error(
          `[ZERODIAG scan ${kind}] INCOMPLETE chain ${event.chain.id} at progress ${event.progress} — scan stalled/aborted`
        )
      }
    }
    const onUtxo = mkLog('utxo')
    const onTxid = mkLog('txid')
    railgunEmitter.on('utxoScanUpdate', onUtxo)
    railgunEmitter.on('txidScanUpdate', onTxid)
    return () => {
      railgunEmitter.off('utxoScanUpdate', onUtxo)
      railgunEmitter.off('txidScanUpdate', onTxid)
    }
  }, [])

  // Phase 2.3 (Slice C) — UI no longer issues `WALLET_GET_BALANCE` against the offscreen.
  // `fetchBalance` becomes a thin trigger that dispatches the SW pipeline; results flow back
  // via the `balanceSummaries` and `balanceMapsByAccount` controller-state mirrors above,
  // and per-chain progress comes from the live `balanceUpdate` event listener below.
  // Pricing, merkle-root verification, retry semantics and `cachePoisoned` flagging all live
  // in [railgunBackground.ts](#refreshAllBalances) — UI is a pure renderer.
  const fetchBalance = useCallback(async (): Promise<void> => {
    if (!isPrivateModeRef.current) return
    if (!railgunEngineInitialized) return
    if (!account) return
    if (!walletInstances.has(account.addr.toLowerCase())) return

    // In-flight guard MUST run before `setIsBalanceFetching(true)`. A concurrent
    // call (e.g. the scan-Complete auto-refetch firing while the prior chain's
    // getBalance is still pending) would otherwise flip the spinner on, hit the
    // guard's early `return`, and run the `finally` that flips it back off —
    // killing the refresh spinner mid-scan while the first fetch is genuinely
    // still in flight.
    if (!isExtension && inPageFetchInFlightRef.current) {
      console.log('[RailgunUI fetchBalance] in-page fetch already in flight — skipping')
      return
    }

    setIsBalanceFetching(true)
    setIsPriceFetching(false)
    setIsBalanceFetchingError(false)
    try {
      // Phase 2.3 (Slice C) routes balance refresh through the SW's
      // `RAILGUN_BACKGROUND_CONTROLLER_REFRESH_BALANCES` pipeline. On the
      // mobile-as-webapp target there is no SW — the engine runs in-page —
      // so dispatching that action is a no-op (the stub controller registered
      // in WebViewWorker.web.tsx swallows it) and no scan ever kicks off,
      // leaving the dashboard stuck at "0% — Please allow up to 15 minutes".
      // Drive the SDK directly when running outside an extension so
      // `refreshBalances` fires for each chain and emits utxo/txid scan
      // events that drive the progress UI.
      if (!isExtension) {
        const walletInstance = walletInstances.get(account.addr.toLowerCase())
        if (!walletInstance) {
          console.warn('[RailgunUI fetchBalance] no wallet instance for', account.addr)
          return
        }
        inPageFetchInFlightRef.current = true
        const networks = getNetworksForBalance(resolvePrivateBalanceFilter(dashboardNetworkFilter))
        console.log('[RailgunUI fetchBalance] in-page direct call', {
          account: account.addr.slice(0, 10),
          networks
        })
        try {
          // `wallet.getBalance` internally calls SDK `refreshBalances(chain, [walletId])`
          // per network, which triggers the merkletree scan; scan progress flows
          // through `utxoScanUpdate`/`txidScanUpdate` callbacks set in railgunCore.
          const result = await walletInstance.getBalance(networks)
          console.log('[RailgunUI fetchBalance] in-page result', {
            balanceChains: Array.from(result.balance.keys()),
            pendingChains: Array.from(result.pendingBalance.keys()),
            balanceSizes: Array.from(result.balance.entries()).map(
              ([cid, arr]) => [cid, arr.length] as const
            )
          })
          // TEMP ZERODIAG: flat per-bucket sizes so a CDP log capture can read the
          // numbers (confirms whether getBalance returned an empty result that the
          // merge below then commits over a good balance).
          {
            const sz = (mp: Map<number, Array<unknown>>) =>
              JSON.stringify(Array.from(mp.entries()).map(([c, a]) => [c, a.length]))
            // eslint-disable-next-line no-console
            console.log(
              '[ZERODIAG fetch] spend=' +
                sz(result.balance) +
                ' pending=' +
                sz(result.pendingBalance) +
                ' missInt=' +
                sz(result.missingInternalPOIBalance) +
                ' missExt=' +
                sz(result.missingExternalPOIBalance)
            )
          }
          // Apply result directly into railgunWallets state. The SW path's
          // controller-state mirror does this on extension; on mobile-as-webapp
          // there is no SW, and the SDK's `balanceUpdate` callback may not
          // re-emit for already-cached buckets, so UI rows stay empty unless we
          // seed them from the explicit `getBalance` result.
          const targetAddrLower = account.addr.toLowerCase()
          const targetWalletId = walletInstance.getWalletId()
          const toBigintBalances = (
            arr: Array<{ tokenAddress: string; amount: string | bigint }>
          ) => arr.map((a) => ({ tokenAddress: a.tokenAddress, amount: BigInt(a.amount) }))
          const mergeMap = (
            prev: RailgunTokenBalanceMap | undefined,
            incoming: Map<number, Array<{ tokenAddress: string; amount: string | bigint }>>
          ): RailgunTokenBalanceMap => {
            const next: RailgunTokenBalanceMap = new Map(prev ?? new Map())
            for (const [chainId, balances] of incoming) {
              next.set(chainId, toBigintBalances(balances))
            }
            return next
          }
          const mergedBalanceTokenMap = mergeMap(undefined, result.balance)
          const mergedPendingBalanceTokenMap = mergeMap(undefined, result.pendingBalance)
          const mergedMissingInternalPOI = mergeMap(undefined, result.missingInternalPOIBalance)
          const mergedMissingExternalPOI = mergeMap(undefined, result.missingExternalPOIBalance)
          // Fetch USD prices for every token touched across all buckets so the
          // tokens list (which short-circuits on missing prices) renders + total
          // headline computes. SW path does this via `fetchTokenPricesForBalanceMap`
          // inside `#refreshAllBalances`; mobile-web has no SW so do it here.
          const allTokensMerged = combineRailgunBucketBalances(
            combineRailgunBucketBalances(mergedBalanceTokenMap, mergedPendingBalanceTokenMap),
            combineRailgunBucketBalances(mergedMissingInternalPOI, mergedMissingExternalPOI)
          )
          let balanceTokenPriceMap: Map<string, number> = new Map()
          try {
            balanceTokenPriceMap = await fetchTokenPricesForBalanceMap(allTokensMerged)
            console.log('[RailgunUI fetchBalance] prices fetched', {
              priceCount: balanceTokenPriceMap.size
            })
          } catch (err) {
            console.warn('[RailgunUI fetchBalance] price fetch failed', err)
          }
          const mergedForTotal = combineRailgunBucketBalances(
            mergedBalanceTokenMap,
            mergedPendingBalanceTokenMap
          )
          const totalBalanceUSD = getTotalBalanceUSD(mergedForTotal, balanceTokenPriceMap)
          setRailgunWallets((prev) => {
            console.log('[RailgunUI fetchBalance] applying merge', {
              prevCount: prev.length,
              prevAddrs: prev.map((w) => w.accountAddr.toLowerCase().slice(0, 10)),
              targetAddr: targetAddrLower.slice(0, 10),
              targetWalletId: targetWalletId.slice(0, 10),
              priceCount: balanceTokenPriceMap.size,
              totalBalanceUSD
            })
            return prev.map((wallet) => {
              if (wallet.accountAddr.toLowerCase() !== targetAddrLower) return wallet
              if (wallet.walletId && wallet.walletId !== targetWalletId) return wallet
              console.log('[RailgunUI fetchBalance] merged onto wallet', {
                accountAddr: wallet.accountAddr.slice(0, 10),
                walletId: wallet.walletId?.slice(0, 10),
                balanceChains: Array.from(result.balance.keys())
              })
              return {
                ...wallet,
                balanceTokenMap: mergedBalanceTokenMap,
                pendingBalanceTokenMap: mergedPendingBalanceTokenMap,
                missingInternalPOIBalanceTokenMap: mergedMissingInternalPOI,
                missingExternalPOIBalanceTokenMap: mergedMissingExternalPOI,
                balanceTokenPriceMap,
                totalBalanceUSD,
                balanceLastUpdatedAt: Date.now()
              }
            })
          })
          // Mark every chain this fetch covered as "first sync done" for this
          // account. Keyed per chain so switching to a different (unscanned) chain
          // still reports `isInitialPrivateSync = true`.
          const completedAddr = account.addr.toLowerCase()
          const completedKeys = chainIdsForNetworks(networks).map((cid) =>
            inPageChainKey(completedAddr, cid)
          )
          setInPageFetchCompletedChainKeys((prev) => {
            if (completedKeys.every((k) => prev.has(k))) return prev
            const next = new Set(prev)
            completedKeys.forEach((k) => next.add(k))
            return next
          })
        } finally {
          inPageFetchInFlightRef.current = false
        }
      } else {
        dispatch({ type: 'RAILGUN_BACKGROUND_CONTROLLER_REFRESH_BALANCES' })
      }
    } catch (error) {
      console.error('[RailgunUI fetchBalance] dispatch failed:', error)
      if (isPrivateModeRef.current) setIsBalanceFetchingError(true)
    } finally {
      // The SW pipeline runs asynchronously; flipping the indicator off immediately matches
      // the previous UX (the dashboard shows live values from incremental balanceUpdate events
      // and the controller-state mirror as they land). The summary mirror's `refreshedAt`
      // timestamp is what observers should watch for "did the latest sync land".
      setIsBalanceFetching(false)
      setIsPriceFetching(false)
    }
  }, [account, walletInstances, railgunEngineInitialized, dispatch, dashboardNetworkFilter])

  const getPrivateTransactionHistory = useCallback(async (): Promise<
    Array<TransactionHistoryItem & { networkName: NetworkName }>
  > => {
    if (!account || !walletInstances.has(account.addr.toLowerCase())) return []
    const railgunWallet = walletInstances.get(account.addr.toLowerCase())
    if (!railgunWallet) return []
    const networks = getNetworksForBalance(resolvePrivateBalanceFilter(dashboardNetworkFilter))
    const results = await Promise.all(
      networks.map(async (networkName) => {
        const items = (await railgunWallet.getTransactionHistory(
          networkName
        )) as TransactionHistoryItem[]
        return items.map((item) => ({ ...item, networkName }))
      })
    )
    const combined = results.flat().sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))
    return combined
  }, [account, walletInstances, dashboardNetworkFilter])

  // Tracks txids previously seen so refreshPrivateHistory can detect newly-arrived
  // entries and trigger a balance refresh. Without this, the Tokens tab's
  // "+pending POI" suffix lags behind the Activity tab until the next full sync.
  // Keyed by account address so account switches don't false-positive every entry as new.
  const seenPrivateHistoryTxidsRef = useRef<Map<string, Set<string>>>(new Map())

  // Last-started timestamp per `${account}:${filter}` so back-to-back *automatic*
  // sweeps collapse to one. The in-flight guard only catches temporally-overlapping
  // calls; on entering private mode three separate effects (enter-private,
  // Activity-tab, isPrivate-screen) each call refreshPrivateHistory as state
  // settles, spread across seconds — they don't overlap, so each ran a full
  // 3-chain worker sweep. Within the window the data is already fresh (a sweep
  // just ran for the same account+filter), so a repeat auto-call is pure waste.
  // Manual refreshes (pull-to-refresh / refresh button) pass `force` to bypass.
  const lastHistorySweepAtRef = useRef<Map<string, number>>(new Map())
  const HISTORY_SWEEP_DEDUP_WINDOW_MS = 8000

  const refreshPrivateHistory = useCallback(
    async (opts?: { force?: boolean }) => {
      if (!account || !walletInstances.has(account.addr.toLowerCase())) {
        setPrivateHistory([])
        setPrivateHistoryError(null)
        setIsPrivateHistoryLoading(false)
        return
      }

      const accountKey = account.addr.toLowerCase()
      // Dedup key includes the filter: switching account or network must always
      // re-sweep (different data), only same-account+same-filter is collapsed.
      const sweepKey = `${accountKey}:${String(dashboardNetworkFilter)}`

      // Overlapping sweeps for the SAME key are pure waste — the result is
      // idempotent and the consumers read the shared `privateHistory` state, not
      // this call's return. A different key means different data: queue one
      // re-sweep for when the in-flight one finishes instead of dropping it.
      if (historyFetchInFlightRef.current) {
        if (historyFetchInFlightKeyRef.current !== sweepKey) {
          pendingHistorySweepRef.current = true
          console.log(
            '[RailgunUI refreshPrivateHistory] sweep in flight for a different account+filter — queued re-sweep'
          )
        } else {
          console.log('[RailgunUI refreshPrivateHistory] sweep already in flight — skipping')
        }
        return
      }
      if (!opts?.force) {
        const last = lastHistorySweepAtRef.current.get(sweepKey)
        if (last != null && Date.now() - last < HISTORY_SWEEP_DEDUP_WINDOW_MS) {
          console.log(
            '[RailgunUI refreshPrivateHistory] swept <8s ago for this account+filter — skipping (auto)'
          )
          return
        }
      }
      lastHistorySweepAtRef.current.set(sweepKey, Date.now())

      const cacheKey = `railgunPrivateHistoryCache:${accountKey}`
      historyFetchInFlightRef.current = true
      historyFetchInFlightKeyRef.current = sweepKey
      setIsPrivateHistoryLoading(true)
      setPrivateHistoryError(null)
      try {
        const items = await getPrivateTransactionHistory()
        setPrivateHistory(items)
        void storage.set(cacheKey, items).catch(() => {})

        const seenByAccount = seenPrivateHistoryTxidsRef.current
        const seen = seenByAccount.get(accountKey)
        const isFirstFill = seen == null
        const nextSeen = new Set<string>()
        let newCount = 0
        for (const item of items) {
          const txid = item.txid
          if (!txid) continue
          nextSeen.add(txid)
          if (seen && !seen.has(txid)) newCount += 1
        }
        seenByAccount.set(accountKey, nextSeen)

        // Skip the very first fill for this account (entering private mode hydrates
        // the list — those are not "new"). Only trigger when an entry appears
        // mid-session and we aren't already in a fetch.
        if (!isFirstFill && newCount > 0 && !isBalanceFetchingRef.current) {
          console.log(
            `[RailgunUI] privateHistory gained ${newCount} new tx — triggering fetchBalance to refresh pending POI buckets`
          )
          const fetchFn = fetchBalanceRef.current
          if (fetchFn) void fetchFn()
        }
      } catch (err) {
        console.error('Failed to fetch private transaction history:', err)
        setPrivateHistoryError('Failed to load transaction history.')
        setPrivateHistory([])
      } finally {
        historyFetchInFlightRef.current = false
        historyFetchInFlightKeyRef.current = null
        setIsPrivateHistoryLoading(false)
        // Re-fire the sweep that arrived for a different account+filter while this
        // one was running. Via the ref so it uses the LATEST callback (fresh
        // filter), not this closure's; its own dedup guards bound the recursion.
        if (pendingHistorySweepRef.current) {
          pendingHistorySweepRef.current = false
          void refreshPrivateHistoryRef.current()
        }
      }
    },
    [account, walletInstances, getPrivateTransactionHistory, dashboardNetworkFilter]
  )

  // Hydrate cached private history immediately on account change so popup reopens
  // paint the previous list instead of an empty skeleton while the SDK fetch runs.
  const prevHydratedAccountRef = useRef<string | null>(null)
  useEffect(() => {
    if (!account?.addr) return
    const accountKey = account.addr.toLowerCase()
    const prevAccount = prevHydratedAccountRef.current
    const accountChanged = prevAccount != null && prevAccount !== accountKey
    prevHydratedAccountRef.current = accountKey
    const cacheKey = `railgunPrivateHistoryCache:${accountKey}`
    let cancelled = false
    // On a REAL account switch the on-screen list belongs to the PREVIOUS account —
    // drop it immediately so stale rows don't linger while the new account's fetch
    // runs (the `prev.length > 0 ? prev` guard below would otherwise keep them, since
    // it can't tell a popup remount apart from an in-place account switch).
    if (accountChanged) setPrivateHistory([])
    void storage
      .get(cacheKey, [])
      .then((cached) => {
        if (cancelled) return
        const items = cached as Array<TransactionHistoryItem & { networkName: NetworkName }>
        if (!Array.isArray(items) || items.length === 0) return
        // After a switch we just cleared the list, so adopt the new account's cache;
        // on first mount keep whatever is already there to avoid an empty flash.
        setPrivateHistory((prev) => (accountChanged || prev.length === 0 ? items : prev))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [account?.addr])

  // Keep refreshPrivateHistory ref in sync
  const refreshPrivateHistoryRef = useRef(refreshPrivateHistory)
  useEffect(() => {
    refreshPrivateHistoryRef.current = refreshPrivateHistory
  }, [refreshPrivateHistory])

  // Keep refs in sync
  useEffect(() => {
    fetchBalanceRef.current = fetchBalance
  }, [fetchBalance])
  useEffect(() => {
    railgunWalletsRef.current = railgunWallets
  }, [railgunWallets])

  // Keep wasPrivateMode ref in sync
  // Refresh private history when entering private mode
  const wasPrivateModeRef = useRef(false)
  useEffect(() => {
    if (!isPrivateMode) {
      wasPrivateModeRef.current = false
      return
    }
    if (!railgunEngineInitialized || !account?.addr || !firstFetchDone || !isUnlocked) return
    if (!walletInstances.has(account.addr.toLowerCase())) return

    const justEnteredPrivate = !wasPrivateModeRef.current
    wasPrivateModeRef.current = true

    if (justEnteredPrivate) {
      void refreshPrivateHistoryRef.current()
    }
  }, [
    isPrivateMode,
    railgunEngineInitialized,
    account?.addr,
    firstFetchDone,
    isUnlocked,
    walletInstances
  ])

  // When user switches to private and we have a wallet instance, fetch balance only on first switch (never again on subsequent switches).
  useEffect(() => {
    if (!isPrivateMode) return
    if (!railgunEngineInitialized || !account?.addr || !firstFetchDone || !isUnlocked) return
    if (hasFetchedBalanceThisPrivateSessionRef.current) return
    if (!walletInstances.has(account.addr.toLowerCase())) return

    hasFetchedBalanceThisPrivateSessionRef.current = true
    // Record the scope so the filter-change effect serves it from cache on a
    // later re-entry instead of refetching (avoids a duplicate fetch here too).
    fetchedPrivateScopesRef.current.add(`${account.addr.toLowerCase()}:${dashboardNetworkFilter}`)
    const fetchFn = fetchBalanceRef.current
    if (fetchFn) void fetchFn()
  }, [
    isPrivateMode,
    railgunEngineInitialized,
    account?.addr,
    firstFetchDone,
    isUnlocked,
    walletInstances,
    dashboardNetworkFilter
  ])

  // Phase 2.1: clear the once-per-private-session fetch latch when the wallet is
  // re-unlocked (isUnlocked false→true). Without this, after an auto-lock +
  // unlock while staying in private mode, the effect above stays latched and
  // the private balance never refetches — the UI freezes on the stale balance
  // until the user leaves and re-enters private mode. Independent of auto-lock
  // (any re-unlock benefits), but auto-lock makes it routine.
  const prevIsUnlockedRef = useRef(isUnlocked)
  useEffect(() => {
    const wasUnlocked = prevIsUnlockedRef.current
    prevIsUnlockedRef.current = isUnlocked
    if (!wasUnlocked && isUnlocked) {
      hasFetchedBalanceThisPrivateSessionRef.current = false
      // Re-arm per-scope fetching for the fresh session.
      fetchedPrivateScopesRef.current.clear()
    }
  }, [isUnlocked])

  // Auto-apply engine balance updates without re-entering the engine.
  //
  // The offscreen Railgun engine emits a `balanceUpdate` per (chain, bucket) whenever a UTXO
  // change is observed (post-shield, post-receive, post-POI-resolve). The offscreen forwards it
  // over the OFFSCREEN_EVENT port to the UI's `railgunOffscreenClient`, which re-emits via
  // `railgunEmitter`. Without this listener the UI only refreshes balance on explicit user
  // actions (mode switch, account switch, network filter change, manual reload), so a shield
  // or incoming receive sits invisible until reload.
  //
  // SAFETY: this handler must NEVER call back into the engine (`getBalance`, `refreshBalances`,
  // etc.). A previous attempt listening to `poiProofProgress` did so and looped: getBalance →
  // refreshBalances → emits more events → handler fires again. Here we only do React setState
  // and a single chrome.storage.local write — neither path can re-emit engine events.
  //
  // The event payload IS the authoritative balance for `(chain, bucket)` (same shape and
  // semantics that `getBalance` reads internally — see railgunWallet.ts setOnBalanceUpdateCallback).
  // We replace that chain's entry in the matching bucket map and recompute totalBalanceUSD from
  // the existing price map. Prices are NOT refetched — explicit `fetchBalance` paths still own
  // pricing refresh, which avoids any network call from this listener.
  //
  // Events are coalesced over a 250ms debounce window so a rapid Spendable→MissingInternalPOI
  // sequence applied as one state update; otherwise the dashboard total briefly drops between
  // events (Spendable is emitted before pending-POI buckets for the same chain).
  useEffect(() => {
    if (!isPrivateMode) return

    const pending = new Map<string, RailgunBalancesEvent[]>()
    let timer: ReturnType<typeof setTimeout> | null = null

    const flush = () => {
      timer = null
      if (pending.size === 0) return
      const grouped = Array.from(pending.values()).flat()
      pending.clear()

      setRailgunWallets((prev) => {
        let changed = false
        const next = prev.map((wallet) => {
          if (!wallet.walletId) return wallet
          const eventsForWallet = grouped.filter((e) => e.railgunWalletID === wallet.walletId)
          if (eventsForWallet.length === 0) return wallet

          let balanceTokenMap = wallet.balanceTokenMap ?? new Map()
          let pendingBalanceTokenMap = wallet.pendingBalanceTokenMap ?? new Map()
          let missingInternalPOIBalanceTokenMap =
            wallet.missingInternalPOIBalanceTokenMap ?? new Map()
          let missingExternalPOIBalanceTokenMap =
            wallet.missingExternalPOIBalanceTokenMap ?? new Map()

          for (const event of eventsForWallet) {
            const chainId = Number(event.chain.id)
            const balances = event.erc20Amounts.map((a) => ({
              tokenAddress: a.tokenAddress,
              amount: BigInt(a.amount)
            }))
            const replaceBucket = (map: RailgunTokenBalanceMap): RailgunTokenBalanceMap => {
              const copy = new Map(map)
              copy.set(chainId, balances)
              return copy
            }
            // TEMP ZERODIAG (parallel-scan balance-zeroing hunt): log every bucket
            // write with incoming vs prev token count, flag empty-overwrites.
            {
              const prevMap =
                event.balanceBucket === 'Spendable'
                  ? balanceTokenMap
                  : event.balanceBucket === 'ShieldPending'
                    ? pendingBalanceTokenMap
                    : event.balanceBucket === 'MissingInternalPOI'
                      ? missingInternalPOIBalanceTokenMap
                      : missingExternalPOIBalanceTokenMap
              const prevCount = prevMap.get(chainId)?.length ?? 0
              // eslint-disable-next-line no-console
              console.log('[ZERODIAG balUpd]', {
                wallet: wallet.walletId?.slice(0, 8),
                chain: chainId,
                bucket: event.balanceBucket,
                incoming: balances.length,
                prev: prevCount,
                EMPTY_OVERWRITE: prevCount > 0 && balances.length === 0
              })
            }
            switch (event.balanceBucket) {
              case 'Spendable':
                balanceTokenMap = replaceBucket(balanceTokenMap)
                break
              case 'ShieldPending':
                pendingBalanceTokenMap = replaceBucket(pendingBalanceTokenMap)
                break
              case 'MissingInternalPOI':
                missingInternalPOIBalanceTokenMap = replaceBucket(missingInternalPOIBalanceTokenMap)
                break
              case 'MissingExternalPOI':
                missingExternalPOIBalanceTokenMap = replaceBucket(missingExternalPOIBalanceTokenMap)
                break
              default:
                break
            }
          }

          // Headline = Spendable + ShieldPending only, matching main's `useRailgunOverviewBalance`.
          // Missing-POI is surfaced separately on the dashboard, so don't fold it in here.
          const mergedForTotal = combineRailgunBucketBalances(
            balanceTokenMap,
            pendingBalanceTokenMap
          )
          // Only refresh totalBalanceUSD if the existing price map covers every token in the
          // merged maps. Otherwise we'd race with `fetchBalance`'s pricing step (which fetches
          // prices AFTER updating bucket maps) and briefly overwrite the displayed total with
          // $0. Preserving the previous total is correct: when fetchBalance lands prices, its
          // own setState will recompute the headline.
          const priceMap = wallet.balanceTokenPriceMap
          let totalBalanceUSD = wallet.totalBalanceUSD
          let totalRecomputed = false
          if (priceMap && priceMap.size > 0) {
            let allPriced = true
            for (const [chainId, balances] of mergedForTotal) {
              for (const b of balances) {
                if (b.amount === 0n) continue
                if (!priceMap.has(priceKey(chainId, b.tokenAddress))) {
                  allPriced = false
                  break
                }
              }
              if (!allPriced) break
            }
            if (allPriced) {
              totalBalanceUSD = getTotalBalanceUSD(mergedForTotal, priceMap)
              totalRecomputed = true
            }
          }
          const balanceLastUpdatedAt = Date.now()

          changed = true

          if (totalRecomputed && typeof totalBalanceUSD === 'number') {
            // Phase 2.3 — UI does not persist; SW row is the source of truth via
            // `#persistSummaryOntoWalletRow`.
            if (totalBalanceUSD !== wallet.totalBalanceUSD) {
              console.log('[RailgunUI total←balanceUpdate-listener]', {
                account: wallet.accountAddr.slice(0, 10),
                from: wallet.totalBalanceUSD,
                to: totalBalanceUSD,
                eventChainIds: Array.from(new Set(eventsForWallet.map((e) => Number(e.chain.id)))),
                eventBuckets: Array.from(new Set(eventsForWallet.map((e) => e.balanceBucket))),
                spendableMapNow: Array.from(balanceTokenMap.entries()).map(
                  ([cid, arr]) => [cid, arr.length] as const
                )
              })
            }
          }

          return {
            ...wallet,
            balanceTokenMap,
            pendingBalanceTokenMap,
            missingInternalPOIBalanceTokenMap,
            missingExternalPOIBalanceTokenMap,
            ...(totalRecomputed ? { totalBalanceUSD, balanceLastUpdatedAt } : {})
          }
        })
        return changed ? next : prev
      })
    }

    const handle = (event: RailgunBalancesEvent) => {
      console.log('[RailgunUI balanceUpdate-listener] received', {
        walletID: event.railgunWalletID?.slice(0, 10),
        chain: event.chain?.id,
        bucket: event.balanceBucket,
        tokens: event.erc20Amounts?.length,
        isPrivateMode: isPrivateModeRef.current
      })
      if (!isPrivateModeRef.current) return
      const key = `${event.railgunWalletID}:${event.chain.id}:${event.balanceBucket}`
      const list = pending.get(key) ?? []
      list.push(event)
      pending.set(key, list)
      if (timer == null) {
        timer = setTimeout(flush, 250)
      }
    }

    railgunEmitter.on('balanceUpdate', handle)
    return () => {
      railgunEmitter.off('balanceUpdate', handle)
      if (timer != null) clearTimeout(timer)
    }
  }, [isPrivateMode])

  // When dashboard network filter changes in private mode, refetch balance for the new network.
  const prevDashboardNetworkFilterRef = useRef(dashboardNetworkFilter)
  useEffect(() => {
    if (!isPrivateMode) return
    if (!railgunEngineInitialized || !account?.addr || !firstFetchDone || !isUnlocked) return
    if (!walletInstances.has(account.addr.toLowerCase())) return
    // Only fetch when filter actually changed (skip initial mount to avoid duplicate with "switch to private" effect)
    if (prevDashboardNetworkFilterRef.current == dashboardNetworkFilter) return
    prevDashboardNetworkFilterRef.current = dashboardNetworkFilter

    // Session-consistent private wallet: a given (account, filter) scope is
    // fetched/scanned once per unlocked session. Toggling public↔private flips
    // the filter back to the private scope, which used to refetch+rescan on every
    // re-entry; if we've already fetched this scope, serve the cached balance (the
    // `balanceUpdate` listener keeps it live) instead of re-running getBalance.
    const scope = `${account.addr.toLowerCase()}:${dashboardNetworkFilter}`
    if (fetchedPrivateScopesRef.current.has(scope)) return
    fetchedPrivateScopesRef.current.add(scope)

    // New chain selection: re-allow one auto-refetch per chain for the late-token
    // catch-up. (Cleared here, not per-chain, so all chains re-arm on any switch.)
    autoRefetchedChainsRef.current.clear()

    const fetchFn = fetchBalanceRef.current
    if (fetchFn) void fetchFn()
  }, [
    isPrivateMode,
    railgunEngineInitialized,
    account?.addr,
    firstFetchDone,
    isUnlocked,
    walletInstances,
    dashboardNetworkFilter
  ])

  // When current wallet (account) changes while in private mode, fetch balance for the new account.
  // Also fetch when a wallet instance just appeared for the current account (e.g. after switching to an account that had none).
  const prevAccountAddrRef = useRef<string | null>(null)
  const prevHadWalletInstanceRef = useRef(false)
  useEffect(() => {
    if (!isPrivateMode) return
    if (!railgunEngineInitialized || !account?.addr || !firstFetchDone || !isUnlocked) return

    const accountAddrLower = account.addr.toLowerCase()
    const hasInstance = walletInstances.has(accountAddrLower)
    const prevAddr = prevAccountAddrRef.current
    const accountChanged = prevAddr != null && prevAddr !== accountAddrLower
    const instanceJustAppeared =
      hasInstance && !prevHadWalletInstanceRef.current && prevAddr != null

    prevAccountAddrRef.current = accountAddrLower
    prevHadWalletInstanceRef.current = hasInstance

    if (hasInstance && (accountChanged || instanceJustAppeared)) {
      // New account: re-arm the per-chain auto-refetch guard (its scan state and
      // notes are unrelated to the previous account's).
      autoRefetchedChainsRef.current.clear()
      // New account → its scopes are unfetched this session; reset and record the
      // scope we're about to fetch so a follow-up filter toggle serves it cached.
      fetchedPrivateScopesRef.current.clear()
      fetchedPrivateScopesRef.current.add(`${accountAddrLower}:${dashboardNetworkFilter}`)
      const fetchFn = fetchBalanceRef.current
      if (fetchFn) void fetchFn()
      // Balance alone isn't enough for the Activity screen — re-sweep the new
      // account's private history too, else it keeps rendering the previous
      // account's transactions (sweepKey is per account+filter, so this fresh
      // account won't hit the 8s dedup window).
      void refreshPrivateHistoryRef.current()
    }
  }, [
    isPrivateMode,
    railgunEngineInitialized,
    account?.addr,
    firstFetchDone,
    isUnlocked,
    walletInstances,
    dashboardNetworkFilter
  ])

  // Retrieve railgun account for selected account: create wallet instance when missing.
  // Only runs in private mode. Does not auto-fetch balance (that's done by the effect above).
  useEffect(() => {
    if (!isPrivateMode) return
    if (!railgunEngineInitialized) return
    if (!account?.addr) return
    if (!firstFetchDone) return
    if (!isUnlocked) return

    const normalizedAddr = account.addr.toLowerCase()

    if (processingAccountRef.current === normalizedAddr) return

    const hasWalletInstance = walletInstances.has(normalizedAddr)

    if (!hasWalletInstance) {
      // Respect the backoff window set after a previous failed attempt, so a
      // transient engine-not-ready error doesn't retry on every re-render.
      if (isWithinWalletInstanceBackoff(walletInstanceRetryRef.current.get(normalizedAddr), Date.now()))
        return

      console.log('no wallet instance found, creating it')
      processingAccountRef.current = normalizedAddr
      setIsLoading(true)

      const createWalletInstance = async () => {
        try {
          let railgunWallet: WalletInstance
          // A1 keystore-isolation (mobile-web): the engine + wallet live in the
          // nested worker; the page-side `loadWalletInstanceById` /
          // `createWalletInstanceFromSeed` helpers run the SDK IN THE PAGE, whose
          // engine is never started under worker isolation — so they throw
          // "RAILGUN Engine not yet initialized" on every retry and private
          // balances never load. Re-create (≡ deterministic load) via the worker
          // instead, mirroring the primary `handleLoadRailgunWallets` path.
          if (FEATURES.railgunWorkerIsolationEnabled) {
            railgunWallet = (await createWalletInstanceViaWorker(account.addr)).wallet
          } else {
            const storedWallet = railgunWalletsRef.current.find(
              (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
            )
            if (storedWallet?.walletId && storedWallet?.encryptionKey) {
              railgunWallet = await loadWalletInstanceById(
                storedWallet.walletId,
                storedWallet.encryptionKey
              )
            } else {
              const retrieveFn = retrieveSeedPhraseRef.current
              if (!retrieveFn) {
                setIsLoading(false)
                return
              }
              const seedPhrase = await retrieveFn(account.addr)
              const created = await createWalletInstanceFromSeed(seedPhrase.seed, seedPhrase.seedPassphrase)
              railgunWallet = created.wallet
            }
          }

          setWalletInstances((prev) => {
            const next = new Map(prev)
            next.set(normalizedAddr, railgunWallet)
            return next
          })
          // Success — drop the backoff state for this account and cancel any
          // pending retry timer.
          walletInstanceRetryRef.current.delete(normalizedAddr)
          if (walletInstanceRetryTimerRef.current) {
            clearTimeout(walletInstanceRetryTimerRef.current)
            walletInstanceRetryTimerRef.current = null
          }
          // Balance fetch is triggered by the "switch to private" effect when wallet appears
        } catch (error) {
          console.error(
            `Failed to create railgun wallet instance for account ${account.addr}:`,
            error
          )
          // Schedule an exponential-backoff retry (1s, 2s, 4s … capped at 30s)
          // instead of hammering on every render.
          const retryState = registerWalletInstanceFailure(
            walletInstanceRetryRef.current,
            normalizedAddr,
            Date.now()
          )
          processingAccountRef.current = null
          // Actively re-fire this effect after the backoff. The old code relied on
          // the `balanceUpdate` churn re-firing it, but that never happens when the
          // FIRST create fails (no instance → no balance events) — so recovery
          // deadlocked. Arm a timer that bumps `walletInstanceRetryTick` (a dep).
          if (walletInstanceRetryTimerRef.current) clearTimeout(walletInstanceRetryTimerRef.current)
          walletInstanceRetryTimerRef.current = scheduleWalletInstanceRetryRerun(
            retryState,
            Date.now(),
            () => setWalletInstanceRetryTick((t) => t + 1)
          )
        } finally {
          setIsLoading(false)
        }
      }

      void createWalletInstance()
    }
  }, [
    isPrivateMode,
    railgunEngineInitialized,
    account?.addr,
    firstFetchDone,
    isUnlocked,
    walletInstances,
    createWalletInstanceViaWorker,
    // Bumped by the failure-retry timer so a deadlocked first-create self-heals.
    walletInstanceRetryTick
  ])

  // Cancel a pending wallet-instance retry timer on unmount.
  useEffect(
    () => () => {
      if (walletInstanceRetryTimerRef.current) clearTimeout(walletInstanceRetryTimerRef.current)
    },
    []
  )

  const shieldTo = useCallback(
    async (params: ShieldParams) => {
      if (!account?.addr) return

      // Mirror `estimateShieldGas`: a shield to an explicit 0zk recipient does
      // not need the *live* wallet instance (the public EOA signs/broadcasts and
      // the destination is the recipient). Resolve the wallet id from persisted
      // metadata so a still-initializing private wallet doesn't silently drop the
      // shield. Only require the live instance for a self-shield with no
      // recipient (its own 0zk address must be derived from it).
      const storedWalletId = railgunWalletsRef.current.find(
        (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
      )?.walletId
      const railgunWallet = walletInstances.get(account.addr.toLowerCase()) ?? null
      const walletId = storedWalletId ?? railgunWallet?.getWalletId()
      if (!params.recipientAddress && !railgunWallet) return

      const isNativeOrWrappedBase =
        params.tokenAddress == null ||
        params.tokenAddress.toLowerCase() === ZERO_ADDRESS.toLowerCase() ||
        isWrappedBaseToken(params.chainId, params.tokenAddress)

      // A1 milestone 5: sign + broadcast the public shield tx in the worker.
      // The page sends only seedId + non-secret params; the worker resolves the
      // 0zk destination, signs and broadcasts, and refreshes POIs (engine lives
      // in the worker). The page records the tx with the returned 0zk address.
      if (FEATURES.railgunWorkerIsolationEnabled) {
        const seedId = resolveSeedIdForAccount(account.addr)
        const { txHash, railgunAddress } = await railgunWorkerRpc('shieldExecute', {
          seedId,
          railgunWalletID: walletId,
          recipientRailgunAddress: params.recipientAddress,
          isBaseToken: isNativeOrWrappedBase,
          chainId: params.chainId,
          amount: params.amount,
          tokenAddress: params.tokenAddress,
          feeSpeed: params.feeSpeed
        })
        void appendRailgunShieldTxRecord({
          txHash,
          chainId: params.chainId,
          amount: params.amount.toString(),
          recipientAddress: railgunAddress,
          accountAddr: account.addr,
          tokenAddress:
            !isNativeOrWrappedBase && params.tokenAddress ? params.tokenAddress : undefined,
          feeSpeed: params.feeSpeed,
          timestamp: Date.now()
        })
        return
      }

      const seedPhrase = await retrieveSeedPhrase(account.addr)
      const railgunDestinationAddress =
        params.recipientAddress || (railgunWallet ? await railgunWallet.getRailgunAddress() : null)
      if (!railgunDestinationAddress) return

      let txHash: string
      if (!isNativeOrWrappedBase && params.tokenAddress) {
        txHash = await shieldERC20WithSDK({
          mnemonic: seedPhrase.seed,
          passphrase: seedPhrase.seedPassphrase,
          chainId: params.chainId,
          amount: params.amount,
          tokenAddress: params.tokenAddress,
          railgunWalletAddress: railgunDestinationAddress,
          feeSpeed: params.feeSpeed
        })
      } else {
        txHash = await shieldBaseTokenWithSDK({
          mnemonic: seedPhrase.seed,
          passphrase: seedPhrase.seedPassphrase,
          chainId: params.chainId,
          amount: params.amount,
          railgunWalletAddress: railgunDestinationAddress,
          feeSpeed: params.feeSpeed
        })
      }

      void appendRailgunShieldTxRecord({
        txHash,
        chainId: params.chainId,
        amount: params.amount.toString(),
        recipientAddress: railgunDestinationAddress,
        accountAddr: account.addr,
        tokenAddress:
          !isNativeOrWrappedBase && params.tokenAddress ? params.tokenAddress : undefined,
        feeSpeed: params.feeSpeed,
        timestamp: Date.now()
      })

      const chainId = params.chainId
      if (walletId) {
        void (async () => {
          try {
            if (FEATURES.railgunEngineOffscreenEnabled) {
              await refreshReceivePOIsInOffscreen({ chainId, walletId })
            } else {
              await refreshReceivePOIsForWalletForChain({ chainId, walletId })
            }
          } catch (err) {
            // Best-effort POI refresh; ignore errors.
          }
        })()
      }
    },
    [account, walletInstances, retrieveSeedPhrase, resolveSeedIdForAccount, railgunWorkerRpc]
  )

  const estimateShieldGas = useCallback(
    async (params: ShieldParams) => {
      if (!account?.addr) return null

      // Shielding to an explicit 0zk recipient does NOT need the *live* wallet
      // instance: the public EOA (resolved from seedId) signs/estimates the tx
      // and the destination is the provided recipient. Requiring the live
      // instance here made the estimate silently `return null` — which the UI
      // renders as the misleading generic "Unable to estimate gas. Please try
      // again or use another token." — whenever the private wallet was still
      // initializing (the same condition users see as "private balance won't
      // load"). Resolve the wallet id from the persisted metadata (mirroring the
      // unshield path) and fall back to the live instance's own address only for
      // a self-shield with no recipient.
      const storedWalletId = railgunWalletsRef.current.find(
        (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
      )?.walletId
      const railgunWallet = walletInstances.get(account.addr.toLowerCase()) ?? null
      const walletId = storedWalletId ?? railgunWallet?.getWalletId()
      if (!params.recipientAddress && !railgunWallet) return null

      const isNativeOrWrappedBase =
        params.tokenAddress == null ||
        params.tokenAddress.toLowerCase() === ZERO_ADDRESS.toLowerCase() ||
        isWrappedBaseToken(params.chainId, params.tokenAddress)

      if (FEATURES.railgunWorkerIsolationEnabled) {
        const seedId = resolveSeedIdForAccount(account.addr)
        return railgunWorkerRpc('shieldEstimateGas', {
          seedId,
          railgunWalletID: walletId,
          recipientRailgunAddress: params.recipientAddress,
          isBaseToken: isNativeOrWrappedBase,
          chainId: params.chainId,
          amount: params.amount,
          tokenAddress: params.tokenAddress
        })
      }

      const seedPhrase = await retrieveSeedPhrase(account.addr)
      const railgunDestinationAddress =
        params.recipientAddress || (railgunWallet ? await railgunWallet.getRailgunAddress() : null)
      if (!railgunDestinationAddress) return null

      if (!isNativeOrWrappedBase && params.tokenAddress) {
        return estimateShieldGasERC20({
          mnemonic: seedPhrase.seed,
          passphrase: seedPhrase.seedPassphrase,
          chainId: params.chainId,
          amount: params.amount,
          tokenAddress: params.tokenAddress,
          railgunWalletAddress: railgunDestinationAddress
        })
      }

      return estimateShieldGasBaseToken({
        mnemonic: seedPhrase.seed,
        passphrase: seedPhrase.seedPassphrase,
        chainId: params.chainId,
        amount: params.amount,
        railgunWalletAddress: railgunDestinationAddress
      })
    },
    [account, walletInstances, retrieveSeedPhrase, resolveSeedIdForAccount, railgunWorkerRpc]
  )

  const generateUnshieldProof = useCallback(
    async (params: UnshieldParams): Promise<PreparedUnshieldGasDetails | null> => {
      if (!account?.addr) return null
      if (!walletInstances.has(account.addr.toLowerCase())) return null
      assertCacheNotPoisoned(params.chainId)

      const storedWallet = railgunWalletsRef.current.find(
        (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
      )
      if (!storedWallet?.walletId) {
        throw new Error('Railgun wallet metadata missing for unshield.')
      }

      const recipientAddress = params.recipientAddress || account.addr

      // A1 milestone 5: generate the unshield proof in the worker. The page gets
      // only an opaque preparedUnshieldId (carried through the gas-details slot);
      // spend params + gas details stay bound in the worker for execute.
      if (FEATURES.railgunWorkerIsolationEnabled) {
        const seedId = resolveSeedIdForAccount(account.addr)
        const { preparedUnshieldId } = await railgunWorkerRpc('unshieldGenerateProof', {
          seedId,
          railgunWalletID: storedWallet.walletId,
          isBaseToken: isWrappedBaseToken(params.chainId, params.tokenAddress),
          chainId: params.chainId,
          amount: params.amount,
          tokenAddress: params.tokenAddress,
          recipientAddress,
          feeSpeed: params.feeSpeed
        })
        return {
          __preparedUnshieldId: preparedUnshieldId
        } as unknown as PreparedUnshieldGasDetails
      }

      if (!storedWallet.encryptionKey) {
        throw new Error('Railgun wallet metadata missing for unshield.')
      }
      const seedPhrase = await retrieveSeedPhrase(account.addr)

      if (isWrappedBaseToken(params.chainId, params.tokenAddress)) {
        return generateUnshieldProofBaseTokenWithSDK({
          mnemonic: seedPhrase.seed,
          passphrase: seedPhrase.seedPassphrase,
          chainId: params.chainId,
          amount: params.amount,
          railgunWalletID: storedWallet.walletId,
          encryptionKey: storedWallet.encryptionKey,
          recipientAddress,
          feeSpeed: params.feeSpeed
        })
      }

      return generateUnshieldProofERC20WithSDK({
        mnemonic: seedPhrase.seed,
        passphrase: seedPhrase.seedPassphrase,
        chainId: params.chainId,
        amount: params.amount,
        tokenAddress: params.tokenAddress,
        railgunWalletID: storedWallet.walletId,
        encryptionKey: storedWallet.encryptionKey,
        recipientAddress,
        feeSpeed: params.feeSpeed
      })
    },
    [
      account,
      walletInstances,
      retrieveSeedPhrase,
      assertCacheNotPoisoned,
      resolveSeedIdForAccount,
      railgunWorkerRpc
    ]
  )

  const executeUnshieldFromProof = useCallback(
    async (params: UnshieldParams, gasDetails: PreparedUnshieldGasDetails) => {
      if (!account?.addr) return
      if (!walletInstances.has(account.addr.toLowerCase())) return
      assertCacheNotPoisoned(params.chainId)

      const storedWallet = railgunWalletsRef.current.find(
        (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
      )
      if (!storedWallet?.walletId) {
        throw new Error('Railgun wallet metadata missing for unshield.')
      }

      // A1 milestone 5: broadcast the proven unshield in the worker. Page sends
      // only the opaque preparedUnshieldId + seedId; seed never leaves the worker.
      if (FEATURES.railgunWorkerIsolationEnabled) {
        const preparedUnshieldId = (gasDetails as any)?.__preparedUnshieldId
        if (!preparedUnshieldId) {
          throw new Error('Missing preparedUnshieldId for worker-isolated unshield.')
        }
        const seedId = resolveSeedIdForAccount(account.addr)
        const { txHash } =
          (await railgunWorkerRpc('unshieldExecute', { seedId, preparedUnshieldId })) ?? {}
        // Record the user-entered public recipient keyed by on-chain tx hash. For
        // base-token (e.g. WETH→ETH) unshields the SDK history stores the Relay
        // Adapt contract as `recipientAddress`, so Activity must fall back to this
        // record to show the real destination the user typed.
        if (txHash) {
          void appendRailgunShieldTxRecord({
            txHash,
            chainId: params.chainId,
            amount: params.amount.toString(),
            recipientAddress: params.recipientAddress || account.addr,
            accountAddr: account.addr,
            tokenAddress: params.tokenAddress,
            feeSpeed: params.feeSpeed,
            timestamp: Date.now()
          })
        }
        markUnshieldInFlight(params.chainId, params.tokenAddress)
        return
      }

      if (!storedWallet.encryptionKey) {
        throw new Error('Railgun wallet metadata missing for unshield.')
      }
      const seedPhrase = await retrieveSeedPhrase(account.addr)
      const recipientAddress = params.recipientAddress || account.addr

      let txHash: string | undefined
      if (isWrappedBaseToken(params.chainId, params.tokenAddress)) {
        txHash = await executePreparedUnshieldBaseTokenWithSDK({
          mnemonic: seedPhrase.seed,
          passphrase: seedPhrase.seedPassphrase,
          chainId: params.chainId,
          amount: params.amount,
          railgunWalletID: storedWallet.walletId,
          encryptionKey: storedWallet.encryptionKey,
          recipientAddress,
          gasDetails
        })
      } else {
        txHash = await executePreparedUnshieldERC20WithSDK({
          mnemonic: seedPhrase.seed,
          passphrase: seedPhrase.seedPassphrase,
          chainId: params.chainId,
          amount: params.amount,
          tokenAddress: params.tokenAddress,
          railgunWalletID: storedWallet.walletId,
          encryptionKey: storedWallet.encryptionKey,
          recipientAddress,
          gasDetails
        })
      }
      // See worker branch above: persist the real public recipient keyed by tx
      // hash so Activity can show it instead of the Relay Adapt contract.
      if (txHash) {
        void appendRailgunShieldTxRecord({
          txHash,
          chainId: params.chainId,
          amount: params.amount.toString(),
          recipientAddress,
          accountAddr: account.addr,
          tokenAddress: params.tokenAddress,
          feeSpeed: params.feeSpeed,
          timestamp: Date.now()
        })
      }
      markUnshieldInFlight(params.chainId, params.tokenAddress)
    },
    [
      account,
      walletInstances,
      retrieveSeedPhrase,
      assertCacheNotPoisoned,
      resolveSeedIdForAccount,
      railgunWorkerRpc,
      markUnshieldInFlight
    ]
  )

  const unshieldFrom = useCallback(
    async (params: UnshieldParams) => {
      const gasDetails = await generateUnshieldProof(params)
      if (!gasDetails) return
      await executeUnshieldFromProof(params, gasDetails)
    },
    [generateUnshieldProof, executeUnshieldFromProof]
  )

  const estimateUnshieldGas = useCallback(
    async (params: UnshieldParams) => {
      if (!account?.addr) return null
      if (!walletInstances.has(account.addr.toLowerCase())) return null

      const storedWallet = railgunWalletsRef.current.find(
        (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
      )
      if (!storedWallet?.walletId) {
        return null
      }

      const recipientAddress = params.recipientAddress || account.addr

      if (FEATURES.railgunWorkerIsolationEnabled) {
        const seedId = resolveSeedIdForAccount(account.addr)
        return railgunWorkerRpc('unshieldEstimateGas', {
          seedId,
          railgunWalletID: storedWallet.walletId,
          isBaseToken: isWrappedBaseToken(params.chainId, params.tokenAddress),
          chainId: params.chainId,
          amount: params.amount,
          tokenAddress: params.tokenAddress,
          recipientAddress
        })
      }

      if (!storedWallet.encryptionKey) {
        return null
      }
      const seedPhrase = await retrieveSeedPhrase(account.addr)

      if (isWrappedBaseToken(params.chainId, params.tokenAddress)) {
        return estimateUnshieldGasBaseToken({
          mnemonic: seedPhrase.seed,
          passphrase: seedPhrase.seedPassphrase,
          chainId: params.chainId,
          amount: params.amount,
          railgunWalletID: storedWallet.walletId,
          encryptionKey: storedWallet.encryptionKey,
          recipientAddress
        })
      }

      return estimateUnshieldGasERC20({
        mnemonic: seedPhrase.seed,
        passphrase: seedPhrase.seedPassphrase,
        chainId: params.chainId,
        amount: params.amount,
        tokenAddress: params.tokenAddress,
        railgunWalletID: storedWallet.walletId,
        encryptionKey: storedWallet.encryptionKey,
        recipientAddress
      })
    },
    [account, walletInstances, retrieveSeedPhrase, resolveSeedIdForAccount, railgunWorkerRpc]
  )

  // --- unshield to origin (POI-blocked shield recovery) ---
  // Recipient is SDK-derived from the blocked shield txid; no broadcaster, the
  // user self-signs + self-pays gas. Same worker-isolation vs in-process/offscreen
  // branching as the regular unshield methods above.
  const resolveStoredWalletForUnshield = useCallback(() => {
    if (!account?.addr) return null
    if (!walletInstances.has(account.addr.toLowerCase())) return null
    const storedWallet = railgunWalletsRef.current.find(
      (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
    )
    if (!storedWallet?.walletId) return null
    return storedWallet as typeof storedWallet & { walletId: string }
  }, [account, walletInstances])

  const getUnshieldToOriginRecipients = useCallback(
    async (params: {
      chainId: number
      originalShieldTxid: string
    }): Promise<UnshieldToOriginRecipients | null> => {
      const storedWallet = resolveStoredWalletForUnshield()
      if (!storedWallet) return null

      // getRecipients reads the wallet's own history by txid — no secret needed.
      if (FEATURES.railgunWorkerIsolationEnabled) {
        return railgunWorkerRpc('unshieldToOriginGetRecipients', {
          railgunWalletID: storedWallet.walletId,
          chainId: params.chainId,
          originalShieldTxid: params.originalShieldTxid
        })
      }

      return getUnshieldToOriginRecipientsWithSDK({
        chainId: params.chainId,
        railgunWalletID: storedWallet.walletId,
        originalShieldTxid: params.originalShieldTxid
      })
    },
    [resolveStoredWalletForUnshield, railgunWorkerRpc]
  )

  const estimateUnshieldToOriginGas = useCallback(
    async (params: UnshieldToOriginParams): Promise<UnshieldGasEstimation | null> => {
      const storedWallet = resolveStoredWalletForUnshield()
      if (!storedWallet) return null

      if (FEATURES.railgunWorkerIsolationEnabled) {
        const seedId = resolveSeedIdForAccount(account!.addr)
        return railgunWorkerRpc('unshieldToOriginEstimateGas', {
          seedId,
          railgunWalletID: storedWallet.walletId,
          chainId: params.chainId,
          originalShieldTxid: params.originalShieldTxid,
          recipients: params.recipients
        })
      }

      if (!storedWallet.encryptionKey) return null
      const seedPhrase = await retrieveSeedPhrase(account!.addr)
      return estimateUnshieldToOriginGasWithSDK({
        mnemonic: seedPhrase.seed,
        passphrase: seedPhrase.seedPassphrase,
        chainId: params.chainId,
        railgunWalletID: storedWallet.walletId,
        encryptionKey: storedWallet.encryptionKey,
        originalShieldTxid: params.originalShieldTxid,
        recipients: params.recipients
      })
    },
    [
      account,
      resolveStoredWalletForUnshield,
      retrieveSeedPhrase,
      resolveSeedIdForAccount,
      railgunWorkerRpc
    ]
  )

  const generateUnshieldToOriginProof = useCallback(
    async (params: UnshieldToOriginParams): Promise<PreparedUnshieldToOriginGasDetails | null> => {
      const storedWallet = resolveStoredWalletForUnshield()
      if (!storedWallet) return null
      assertCacheNotPoisoned(params.chainId)

      if (FEATURES.railgunWorkerIsolationEnabled) {
        const seedId = resolveSeedIdForAccount(account!.addr)
        const { preparedUnshieldToOriginId } = await railgunWorkerRpc(
          'unshieldToOriginGenerateProof',
          {
            seedId,
            railgunWalletID: storedWallet.walletId,
            chainId: params.chainId,
            originalShieldTxid: params.originalShieldTxid,
            recipients: params.recipients,
            feeSpeed: params.feeSpeed
          }
        )
        // Page holds only the opaque handle; spend params + gas details stay in worker.
        return {
          __preparedUnshieldToOriginId: preparedUnshieldToOriginId
        } as unknown as PreparedUnshieldToOriginGasDetails
      }

      if (!storedWallet.encryptionKey) {
        throw new Error('Railgun wallet metadata missing for unshield to origin.')
      }
      const seedPhrase = await retrieveSeedPhrase(account!.addr)
      return generateUnshieldToOriginProofWithSDK({
        mnemonic: seedPhrase.seed,
        passphrase: seedPhrase.seedPassphrase,
        chainId: params.chainId,
        railgunWalletID: storedWallet.walletId,
        encryptionKey: storedWallet.encryptionKey,
        originalShieldTxid: params.originalShieldTxid,
        recipients: params.recipients,
        feeSpeed: params.feeSpeed
      })
    },
    [
      account,
      resolveStoredWalletForUnshield,
      retrieveSeedPhrase,
      assertCacheNotPoisoned,
      resolveSeedIdForAccount,
      railgunWorkerRpc
    ]
  )

  const executeUnshieldToOriginFromProof = useCallback(
    async (params: UnshieldToOriginParams, gasDetails: PreparedUnshieldToOriginGasDetails) => {
      const storedWallet = resolveStoredWalletForUnshield()
      if (!storedWallet) return
      assertCacheNotPoisoned(params.chainId)

      if (FEATURES.railgunWorkerIsolationEnabled) {
        const preparedUnshieldToOriginId = (gasDetails as any)?.__preparedUnshieldToOriginId
        if (!preparedUnshieldToOriginId) {
          throw new Error('Missing preparedUnshieldToOriginId for worker-isolated unshield.')
        }
        const seedId = resolveSeedIdForAccount(account!.addr)
        await railgunWorkerRpc('unshieldToOriginExecute', { seedId, preparedUnshieldToOriginId })
        if (params.tokenAddress) markUnshieldInFlight(params.chainId, params.tokenAddress)
        return
      }

      if (!storedWallet.encryptionKey) {
        throw new Error('Railgun wallet metadata missing for unshield to origin.')
      }
      const seedPhrase = await retrieveSeedPhrase(account!.addr)
      await executePreparedUnshieldToOriginWithSDK({
        mnemonic: seedPhrase.seed,
        passphrase: seedPhrase.seedPassphrase,
        chainId: params.chainId,
        railgunWalletID: storedWallet.walletId,
        gasDetails
      })
      if (params.tokenAddress) markUnshieldInFlight(params.chainId, params.tokenAddress)
    },
    [
      account,
      resolveStoredWalletForUnshield,
      retrieveSeedPhrase,
      assertCacheNotPoisoned,
      resolveSeedIdForAccount,
      railgunWorkerRpc,
      markUnshieldInFlight
    ]
  )

  const estimatePrivateTransferGas = useCallback(
    async (params: PrivateTransferParams) => {
      if (!account?.addr) return null
      if (!walletInstances.has(account.addr.toLowerCase())) return null

      const storedWallet = railgunWalletsRef.current.find(
        (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
      )
      if (!storedWallet?.walletId) return null

      try {
        // A1 milestone 4: run estimate in the nested railgun worker. The page sends
        // only the seedId + non-secret params; the keystore worker injects the
        // mnemonic and the encryptionKey is derived inside the railgun worker.
        if (FEATURES.railgunWorkerIsolationEnabled) {
          const seedId = resolveSeedIdForAccount(account.addr)
          return await railgunWorkerRpc('transferEstimateGas', {
            seedId,
            railgunWalletID: storedWallet.walletId,
            chainId: params.chainId,
            tokenAddress: params.tokenAddress,
            amount: params.amount,
            recipientRailgunAddress: params.recipientRailgunAddress,
            memoText: params.memoText
          })
        }

        if (!storedWallet.encryptionKey) return null
        const seedPhrase = await retrieveSeedPhrase(account.addr)
        return await estimatePrivateTransferGasUtil({
          mnemonic: seedPhrase.seed,
          passphrase: seedPhrase.seedPassphrase,
          chainId: params.chainId,
          tokenAddress: params.tokenAddress,
          amount: params.amount,
          railgunWalletID: storedWallet.walletId,
          encryptionKey: storedWallet.encryptionKey,
          recipientRailgunAddress: params.recipientRailgunAddress
        })
      } catch (err) {
        // The estimate reverted with "Note already spent": the selected input note
        // was nullified on-chain but the wallet still lists it as spendable (local
        // spent-state lag, or a shared-note passphrase-collision between the two
        // RAILGUN wallets on this account). Kick a LIGHT incremental balance refresh
        // in the background — it re-scans the fresh blocks, picks up the nullifier
        // and drops the stale note, so the user's retry no longer selects it. This is
        // the cheap refresh (not a full UTXO rescan). Re-throw so the UI surfaces the
        // accurate "syncing, retry" message via getGasEstimateErrorMessage.
        if (isNoteAlreadySpentError(err)) {
          void fetchBalance()
        }
        throw err
      }
    },
    [
      account,
      walletInstances,
      retrieveSeedPhrase,
      resolveSeedIdForAccount,
      railgunWorkerRpc,
      fetchBalance
    ]
  )

  const checkPrivateTransferPopupPreflight = useCallback(
    async ({
      chainId,
      tokenAddress
    }: {
      chainId: number
      tokenAddress: string
    }): Promise<PrivateTransferPopupPreflightCode | null> => {
      if (!account?.addr) return PrivateTransferPopupPreflightCode.STATE_NOT_READY

      const snapshot = (await storage.get(
        PRIVATE_BALANCE_BACKGROUND_SNAPSHOT_STORAGE_KEY,
        {}
      )) as PrivateBalanceSnapshot
      const key = getPrivateBalanceSnapshotEntryKey({
        accountAddr: account.addr,
        chainId,
        tokenAddress
      })
      const entry = snapshot[key]
      if (!entry) return PrivateTransferPopupPreflightCode.STATE_NOT_READY
      // if (Date.now() - entry.updatedAt > PRIVATE_BALANCE_POPUP_SNAPSHOT_MAX_AGE_MS) {
      //   return PrivateTransferPopupPreflightCode.STATE_NOT_READY
      // }
      // if (entry.hasMissingPOI) return PrivateTransferPopupPreflightCode.POI_REQUIRED
      // if (!entry.hasSpendableBalance) return PrivateTransferPopupPreflightCode.NO_PRIVATE_BALANCE
      return null
    },
    [account?.addr]
  )

  const generatePrivateTransferProof = useCallback(
    async (params: PrivateTransferParams): Promise<PreparedPrivateTransferGasDetails | null> => {
      if (!account?.addr) return null
      if (!walletInstances.has(account.addr.toLowerCase())) return null
      assertCacheNotPoisoned(params.chainId)

      const storedWallet = railgunWalletsRef.current.find(
        (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
      )
      if (!storedWallet?.walletId) {
        throw new Error('Railgun wallet metadata missing for private transfer.')
      }

      // A1 milestone 4: generate the proof in the nested railgun worker. The page
      // never sees the gas details or spend params — the worker binds them to an
      // opaque `preparedTransferId` and returns only that. We carry the id through
      // the screen's opaque "gas details" slot so the existing proof→execute flow
      // is unchanged; `executePrivateTransferFromProof` reads it back out.
      if (FEATURES.railgunWorkerIsolationEnabled) {
        const seedId = resolveSeedIdForAccount(account.addr)
        const { preparedTransferId } = await railgunWorkerRpc('transferGenerateProof', {
          seedId,
          railgunWalletID: storedWallet.walletId,
          chainId: params.chainId,
          tokenAddress: params.tokenAddress,
          amount: params.amount,
          recipientRailgunAddress: params.recipientRailgunAddress,
          memoText: params.memoText,
          feeSpeed: params.feeSpeed
        })
        return {
          __preparedTransferId: preparedTransferId
        } as unknown as PreparedPrivateTransferGasDetails
      }

      if (!storedWallet.encryptionKey) {
        throw new Error('Railgun wallet metadata missing for private transfer.')
      }
      const seedPhrase = await retrieveSeedPhrase(account.addr)
      return generatePrivateTransferProofWithSDK({
        mnemonic: seedPhrase.seed,
        passphrase: seedPhrase.seedPassphrase,
        chainId: params.chainId,
        tokenAddress: params.tokenAddress,
        amount: params.amount,
        railgunWalletID: storedWallet.walletId,
        encryptionKey: storedWallet.encryptionKey,
        recipientRailgunAddress: params.recipientRailgunAddress,
        memoText: params.memoText,
        feeSpeed: params.feeSpeed
      })
    },
    [
      account,
      walletInstances,
      retrieveSeedPhrase,
      assertCacheNotPoisoned,
      resolveSeedIdForAccount,
      railgunWorkerRpc
    ]
  )

  const executePrivateTransferFromProof = useCallback(
    async (params: PrivateTransferParams, gasDetails: PreparedPrivateTransferGasDetails) => {
      if (!account?.addr) {
        throw new Error('No selected account for private transfer.')
      }
      if (!walletInstances.has(account.addr.toLowerCase())) {
        throw new Error('Railgun wallet not loaded for private transfer.')
      }
      assertCacheNotPoisoned(params.chainId)

      const storedWallet = railgunWalletsRef.current.find(
        (w) => w.accountAddr.toLowerCase() === account.addr.toLowerCase()
      )
      if (!storedWallet?.walletId) {
        throw new Error('Railgun wallet metadata missing for private transfer.')
      }

      // A1 milestone 5: populate + sign + broadcast inside the nested railgun
      // worker. The page sends only the opaque preparedTransferId (proof params
      // + gas details stay bound in the worker) and the seedId; the keystore
      // worker injects the mnemonic. Neither the seed nor the populated tx ever
      // reach the page — it gets back only the broadcast tx hash.
      if (FEATURES.railgunWorkerIsolationEnabled) {
        const preparedTransferId = (gasDetails as any)?.__preparedTransferId
        if (!preparedTransferId) {
          throw new Error('Missing preparedTransferId for worker-isolated private transfer.')
        }
        const seedId = resolveSeedIdForAccount(account.addr)
        const { txHash } = await railgunWorkerRpc('transferExecute', {
          seedId,
          preparedTransferId
        })
        return txHash
      }

      if (!storedWallet.encryptionKey) {
        throw new Error('Railgun wallet metadata missing for private transfer.')
      }
      const seedPhrase = await retrieveSeedPhrase(account.addr)
      const txHash = await executePreparedPrivateTransferWithSDK({
        mnemonic: seedPhrase.seed,
        passphrase: seedPhrase.seedPassphrase,
        chainId: params.chainId,
        tokenAddress: params.tokenAddress,
        amount: params.amount,
        railgunWalletID: storedWallet.walletId,
        recipientRailgunAddress: params.recipientRailgunAddress,
        memoText: params.memoText,
        gasDetails
      })

      // The send is broadcast, but the recipient won't see the funds until this wallet's
      // transact-proof reaches the POI aggregator. The SDK only auto-submits via a floating
      // post-scan promise that dies if the popup/SW goes away first — so explicitly ask the SW
      // (which outlives the popup) to drive + verify the submission. Best-effort; never blocks
      // the send result.
      dispatch({
        type: 'RAILGUN_BACKGROUND_CONTROLLER_KICK_SPENT_POIS',
        params: { walletId: storedWallet.walletId, chainId: params.chainId }
      })

      return txHash
    },
    [
      account,
      walletInstances,
      retrieveSeedPhrase,
      assertCacheNotPoisoned,
      resolveSeedIdForAccount,
      railgunWorkerRpc,
      dispatch
    ]
  )

  // Phase 2.3 (Slice C) made `fetchBalance` a fire-and-forget dispatch into the SW pipeline,
  // so the local `isBalanceFetching` flag only stays true for the synchronous handoff (~1ms)
  // — it no longer reflects "the SW is still syncing". Without a separate signal, consumers
  // (Tokens skeleton, overview spinner, shield/unshield CTA gating) think a fresh import is
  // already done and render the empty state during the 10+ minute first-time merkle scan.
  // Bridge that gap by deriving "initial private sync still pending" from the controller-
  // state mirror: a wallet exists for the current account but no trustworthy SW summary
  // (`refreshedAt > 0 && !error`) has landed yet. Internal callers (refreshPrivateHistory)
  // keep using the raw local flag so we don't change their semantics.
  const isInitialPrivateSync = useMemo(() => {
    if (!account) return false
    const accountKey = account.addr.toLowerCase()
    if (!walletInstances.has(accountKey)) return false
    // Mobile-as-webapp: no SW, no summary mirror → use the per-CHAIN in-page
    // completion flags we set after `getBalance` resolves. Still "initial sync"
    // if ANY currently-selected chain hasn't completed its first scan yet — so
    // switching to a fresh chain re-shows the syncing UI instead of a stale,
    // empty token list from the previous chain.
    if (!isExtension) {
      const selectedChainIds = chainIdsForNetworks(
        getNetworksForBalance(resolvePrivateBalanceFilter(dashboardNetworkFilter))
      )
      return selectedChainIds.some(
        (cid) => !inPageFetchCompletedChainKeys.has(inPageChainKey(accountKey, cid))
      )
    }
    const summary = swBalanceSummaries?.find((s) => s.accountAddr.toLowerCase() === accountKey)
    return !summary || !summary.refreshedAt || !!summary.error
  }, [
    account,
    walletInstances,
    swBalanceSummaries,
    inPageFetchCompletedChainKeys,
    dashboardNetworkFilter
  ])
  // SW-side in-flight signal mirrored from `RailgunBackgroundController.isSyncing`. Phase 2.3
  // made `fetchBalance` fire-and-forget so the local `isBalanceFetching` flag only stays true
  // for the synchronous handoff (~1ms). Without this OR, the dashboard refresh button shows
  // no spinner during a real resync because the SW work happens asynchronously.
  const swIsSyncing = !!(railgunBgState as any)?.isSyncing
  const exportedIsBalanceFetching = isBalanceFetching || isInitialPrivateSync || swIsSyncing

  const value = useMemo(
    () => ({
      currentRailgunWallet,
      allRailgunWallets,
      isLoading,
      isBalanceFetching: exportedIsBalanceFetching,
      isPriceFetching,
      isBalanceFetchingError,
      privateHistory,
      isPrivateHistoryLoading,
      privateHistoryError,
      refreshPrivateHistory,
      fetchBalance,
      triggerPOISync,
      shieldTo,
      estimateShieldGas,
      unshieldFrom,
      generateUnshieldProof,
      executeUnshieldFromProof,
      estimateUnshieldGas,
      getUnshieldToOriginRecipients,
      estimateUnshieldToOriginGas,
      generateUnshieldToOriginProof,
      executeUnshieldToOriginFromProof,
      estimatePrivateTransferGas,
      generatePrivateTransferProof,
      executePrivateTransferFromProof,
      checkPrivateTransferPopupPreflight,
      getPrivateTransactionHistory,
      isUnshieldInFlight
    }),
    [
      currentRailgunWallet,
      allRailgunWallets,
      isLoading,
      exportedIsBalanceFetching,
      isPriceFetching,
      isBalanceFetchingError,
      privateHistory,
      isPrivateHistoryLoading,
      privateHistoryError,
      refreshPrivateHistory,
      fetchBalance,
      triggerPOISync,
      shieldTo,
      estimateShieldGas,
      unshieldFrom,
      generateUnshieldProof,
      executeUnshieldFromProof,
      estimateUnshieldGas,
      getUnshieldToOriginRecipients,
      estimateUnshieldToOriginGas,
      generateUnshieldToOriginProof,
      executeUnshieldToOriginFromProof,
      estimatePrivateTransferGas,
      generatePrivateTransferProof,
      executePrivateTransferFromProof,
      checkPrivateTransferPopupPreflight,
      getPrivateTransactionHistory,
      isUnshieldInFlight
    ]
  )

  return (
    <RailgunContext.Provider value={value}>
      <RailgunMerkletreeScanProvider railgunEngineInitialized={railgunEngineInitialized}>
        {children}
      </RailgunMerkletreeScanProvider>
      {/* {children} */}
    </RailgunContext.Provider>
  )
}

export { RailgunContextProvider, RailgunContext }
